import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { Routes, Route, Link as RouterLink, Navigate, useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { fetchAllData, fetchJobs, fetchResults, fetchAdmitCards, fetchAnswerKeys, fetchAdmissions, fetchResultById, fetchAdmitCardById, fetchJobById, fetchAnswerKeyById, fetchAdmissionById, prefetchJob, prefetchResult, prefetchAdmitCard, prefetchAnswerKey, prefetchAdmission } from './services/api';
import { API_ENDPOINTS } from './config/api';
import { SeoHead } from './components/SeoHead';
import { SeoFooter } from './components/SeoFooter';
import { 
  Moon, Sun, Download, Eye, Share2, Clock, 
  XCircle, ExternalLink, Settings, Plus, Trash2, Save, X, ArrowUp, ArrowDown,
  ArrowLeft, Globe, Copy, FolderPlus, Menu
} from 'lucide-react';
// Code splitting - lazy load route chunks (smaller initial bundle, faster FCP/LCP)
const SarkariResultLanding = lazy(() => import('./pages/SarkariResultLanding').then(m => ({ default: m.SarkariResultLanding })));
const SarkariResultCheckLanding = lazy(() => import('./pages/SarkariResultCheckLanding').then(m => ({ default: m.SarkariResultCheckLanding })));
const LatestSarkariResultLanding = lazy(() => import('./pages/LatestSarkariResultLanding').then(m => ({ default: m.LatestSarkariResultLanding })));
const SarkariNaukriLanding = lazy(() => import('./pages/SarkariNaukriLanding').then(m => ({ default: m.SarkariNaukriLanding })));
const CategoryLanding = lazy(() => import('./pages/CategoryLanding').then(m => ({ default: m.CategoryLanding })));
const StateLanding = lazy(() => import('./pages/StateLanding').then(m => ({ default: m.StateLanding })));
const AdminTableManager = lazy(() => import('./pages/AdminTableManager').then(m => ({ default: m.default })));
const WordPressStyleAdmin = lazy(() => import('./pages/WordPressStyleAdmin').then(m => ({ default: m.default })));

// ✅ DEFAULT PROFESSIONAL PAGE TEMPLATE - BASE TEMPLATE
const DEFAULT_PROFESSIONAL_TEMPLATE = {
  id: 'default',
  name: 'Default Professional Template',
  description: 'Standard government job portal layout with all essential sections',
  isDefault: true,
  createdAt: new Date().toISOString(),
  sections: [
    {
      id: 'job-title-section',
      title: 'Job Title & Department Section',
      type: 'title-section',
      isRequired: true,
      isEditable: true,
      order: 1,
      content: {
        showUpdatedDate: true,
        showOfficialWebsite: true,
        titleStyle: 'center',
        customText: 'Radio Operator, Radio Mechanic Apply Online'
      }
    },
    {
      id: 'quick-summary',
      title: 'Quick Summary Cards',
      type: 'summary-cards',
      isRequired: true,
      isEditable: true,
      order: 2,
      content: {
        showTotalPosts: true,
        showApplicationFee: true,
        showMaxAge: true,
        cardLayout: '3-column'
      }
    },
    {
      id: 'important-dates-section',
      title: 'Important Dates',
      type: 'important-dates',
      isRequired: true,
      isEditable: true,
      order: 3,
      content: {
        sectionTitle: 'IMPORTANT DATES',
        sectionIcon: '📅',
        fields: [
          { field: 'notificationDate', label: 'Notification Date', required: true },
          { field: 'applicationStart', label: 'Application Start', required: true },
          { field: 'lastDate', label: 'Last Date Apply Online', required: true, highlight: true },
          { field: 'feePaymentDate', label: 'Fee Payment Date', required: true },
          { field: 'correctionDate', label: 'Correction Date', required: false },
          { field: 'admitCard', label: 'Admit Card', required: false },
          { field: 'examDate', label: 'Exam Date', required: false },
          { field: 'resultDate', label: 'Result Date', required: false }
        ],
        showWarningNote: true,
        warningText: 'Candidates are Advised to Verify the details on the Official Website'
      }
    },
    {
      id: 'application-fee-section',
      title: 'Application Fee',
      type: 'application-fee',
      isRequired: true,
      isEditable: true,
      order: 4,
      content: {
        sectionTitle: 'APPLICATION FEE',
        sectionIcon: '💳',
        feeStructure: [
          { category: 'Gen/ OBC/ EWS', field: 'genOBCEWSFees' },
          { category: 'SC/ ST/ Female', field: 'scSTFees' }
        ],
        showPaymentMethods: true,
        paymentMethods: [
          'Credit Card / Debit Card',
          'Net Banking', 
          'Pay Offline Through E-Challan'
        ]
      }
    },
    {
      id: 'age-limit-section',
      title: 'Age Limit',
      type: 'age-limit',
      isRequired: true,
      isEditable: true,
      order: 5,
      content: {
        sectionTitle: 'AGE LIMIT',
        sectionIcon: '👥',
        ageAsOnDate: '23.09.2025',
        showAgeBreakdown: true
      }
    },
    {
      id: 'total-posts-section',
      title: 'Total Posts',
      type: 'total-posts',
      isRequired: true,
      isEditable: true,
      order: 6,
      content: {
        sectionTitle: 'TOTAL POSTS',
        sectionIcon: '📋',
        showPostBreakdown: true,
        postBreakdown: [
          { postName: 'Radio Operator (RO)', field: 'roVacancy' },
          { postName: 'Radio Mechanic (RM)', field: 'rmVacancy' }
        ]
      }
    },
    {
      id: 'eligibility-section',
      title: 'Eligibility Criteria',
      type: 'eligibility-table',
      isRequired: true,
      isEditable: true,
      order: 7,
      content: {
        sectionTitle: 'Eligibility Criteria 2025',
        sectionIcon: '🎓',
        showAsTable: true,
        tableColumns: [
          { label: 'Post Name', width: '25%' },
          { label: 'Total', width: '15%' },
          { label: 'Eligibility Criteria', width: '60%' }
        ],
        eligibilityData: [
          {
            postName: 'BSF Head Constable Radio Operator (RO)',
            totalField: 'roVacancy',
            criteria: '10th Exam Passed OR Equivalent & 2 Years ITI in Radio & Television/Electronics/Computer Operator & Programming Assistant/Data Preparation/etc. OR 12th Passed OR Equivalent with Physics, Chemistry & Mathematics OR Equivalent Eligibility From Any Recognized University/Board/Institutions in India.'
          },
          {
            postName: 'BSF Head Constable Radio Mechanic (RM)', 
            totalField: 'rmVacancy',
            criteria: '10th Exam Passed OR Equivalent & 2 Years ITI in Radio & General Electronics/Computer Operator & Programming Assistant/etc. OR 12th Passed OR Equivalent with Physics, Chemistry & Mathematics OR Equivalent Eligibility From Any Recognized University/Board/Institutions in India.'
          }
        ]
      }
    },
    {
      id: 'salary-section',
      title: 'Salary Details',
      type: 'salary-table',
      isRequired: true,
      isEditable: true,
      order: 8,
      content: {
        sectionTitle: 'Salary 2025',
        sectionIcon: '💰',
        showAsTable: true,
        salaryData: [
          { label: 'Pay Scale', field: 'salary', suffix: 'Per Month' },
          { label: 'Level', value: 'As Per Government Norms' },
          { label: 'Allowances', value: 'HRA, DA, TA and Other Allowances' }
        ]
      }
    }
  ]
};

// ✅ STABLE INPUT COMPONENTS - NO FOCUS LOSS
const StableInput = React.memo(({ 
  type = "text", 
  value, 
  onChange, 
  placeholder, 
  className,
  autoFocus = false,
  onKeyPress,
  ...props 
}) => {
  const inputRef = useRef(null);

  const handleChange = useCallback((e) => {
    onChange(e.target.value);
  }, [onChange]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  return (
    <input
      ref={inputRef}
      type={type}
      value={value}
      onChange={handleChange}
      onKeyPress={onKeyPress}
      placeholder={placeholder}
      className={className}
      autoComplete="off"
      spellCheck="false"
      autoCapitalize="none"
      autoCorrect="off"
      {...props}
    />
  );
});

const StableTextarea = React.memo(({ 
  value, 
  onChange, 
  placeholder, 
  rows = 4,
  className,
  ...props 
}) => {
  const textareaRef = useRef(null);

  const handleChange = useCallback((e) => {
    onChange(e.target.value);
  }, [onChange]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      rows={rows}
      className={className}
      autoComplete="off"
      spellCheck="false"
      autoCapitalize="none"
      autoCorrect="off"
      {...props}
    />
  );
});

const StableSelect = React.memo(({ 
  value, 
  onChange, 
  children,
  className,
  ...props 
}) => {
  const selectRef = useRef(null);

  const handleChange = useCallback((e) => {
    onChange(e.target.value);
  }, [onChange]);

  return (
    <select
      ref={selectRef}
      value={value}
      onChange={handleChange}
      className={className}
      {...props}
    >
      {children}
    </select>
  );
});

const SITE_URL = 'https://www.sarkariresult.cloud';

const GovJobsPortal = () => {
  const location = useLocation();
  const [darkMode, setDarkMode] = useState(false);
  const [currentPage, setCurrentPage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [showAddJobForm, setShowAddJobForm] = useState(false);
  const [adminActiveTab, setAdminActiveTab] = useState('jobs');
  const [editingJob, setEditingJob] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [showMoreResult, setShowMoreResult] = useState(false);
  const [showMoreAdmit, setShowMoreAdmit] = useState(false);
  const [showMoreJobs, setShowMoreJobs] = useState(false);
  const [showMoreAnswerKey, setShowMoreAnswerKey] = useState(false);
  const [showMoreAdmission, setShowMoreAdmission] = useState(false);
  const [resultSearch, setResultSearch] = useState('');
  const [resultSortBy, setResultSortBy] = useState('recent');
  const [admitSearch, setAdmitSearch] = useState('');
  const [admitSortBy, setAdmitSortBy] = useState('recent');
  const [answerSearch, setAnswerSearch] = useState('');
  const [answerSortBy, setAnswerSortBy] = useState('recent');
  const [admissionSearch, setAdmissionSearch] = useState('');
  const [admissionSortBy, setAdmissionSortBy] = useState('recent');
  
  // ✅ MULTIPLE TEMPLATES SYSTEM STATE
  const [professionalTemplates, setProfessionalTemplates] = useState([DEFAULT_PROFESSIONAL_TEMPLATE]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showAddTemplateForm, setShowAddTemplateForm] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  
  // ✅ ALL CUSTOM FIELDS FUNCTIONALITY PRESERVED
  const [customFields, setCustomFields] = useState({
    jobs: [
      { id: 'status', name: 'Status', type: 'select', options: ['Active', 'Inactive', 'Draft'], required: false },
      { id: 'priority', name: 'Priority', type: 'select', options: ['High', 'Medium', 'Low'], required: false },
      { id: 'category', name: 'Category', type: 'select', options: ['Technical', 'Non-Technical', 'Administrative'], required: false }
    ],
    'admit-cards': [],
    results: [
      { id: 'status', name: 'Status', type: 'select', options: ['Declared', 'Awaited'], required: false }
    ],
    'answer-keys': [
      { id: 'status', name: 'Status', type: 'select', options: ['Released', 'Awaited'], required: false }
    ]
  });
  
  const [showFieldManager, setShowFieldManager] = useState(false);
  const [currentSection, setCurrentSection] = useState('jobs');
  const [newField, setNewField] = useState({ id: '', name: '', type: 'text', options: '', required: false });

  // ✅ ENHANCED JOB DATA WITH TEMPLATE SELECTION SUPPORT
  const [jobPosts, setJobPosts] = useState([]);

  // ✅ ALL DATA FROM BACKEND
  const [admitCards, setAdmitCards] = useState([]);
  const [results, setResults] = useState([]);
  const [answerKeys, setAnswerKeys] = useState([]);
  const [admissions, setAdmissions] = useState([]);

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      setApiError(null);
      let res = await fetchAllData();
      if (!res.success) {
        const [jobsRes, resultsRes, admitRes, keysRes, admRes] = await Promise.all([fetchJobs(), fetchResults(), fetchAdmitCards(), fetchAnswerKeys(), fetchAdmissions()]);
        setJobPosts((jobsRes.success && jobsRes.data) ? jobsRes.data : []);
        setResults((resultsRes.success && resultsRes.data) ? resultsRes.data : []);
        setAdmitCards((admitRes.success && admitRes.data) ? admitRes.data : []);
        setAnswerKeys((keysRes.success && keysRes.data) ? keysRes.data : []);
        setAdmissions((admRes.success && admRes.data) ? admRes.data : []);
        if (!jobsRes.success) setApiError(jobsRes.error || 'Failed to fetch');
      } else {
        setJobPosts(res.jobPosts || []);
        setResults(res.results || []);
        setAdmitCards(res.admitCards || []);
        setAnswerKeys(res.answerKeys || []);
        setAdmissions(res.admissions || []);
      }
      setLoading(false);
    };
    loadAllData();
  }, []);

  // ✅ ENHANCED FORM STATES WITH TEMPLATE SELECTION
  const [newJobForm, setNewJobForm] = useState({
    title: '', department: '', location: '', 
    notificationDate: '', applicationStart: '', lastDate: '',
    feePaymentDate: '', correctionDate: '', admitCard: '',
    examDate: '', resultDate: '', genOBCEWSFees: '', scSTFees: '',
    minimumAge: '', maximumAge: '', totalPosts: '',
    eligibilityCriteria: '', salary: '', applicationLink: '',
    urgency: 'medium', isNew: true, status: 'Active',
    selectedTemplateId: null,
    officialWebsite: '',
    roVacancy: '', rmVacancy: '',
    maleHeight: '', femaleHeight: '', maleChest: '', femaleChest: '',
    maleRunning: '', femaleRunning: '', maleLongJump: '', femaleLongJump: '',
    maleHighJump: '', femaleHighJump: ''
  });

  const [newAdmitCardForm, setNewAdmitCardForm] = useState({
    examName: '', department: '', examDate: '', examTime: '', rollNumber: '', 
    status: 'available', downloadDate: '', centerName: '', instructions: ''
  });

  const [newResultForm, setNewResultForm] = useState({
    examName: '', department: '', resultDate: '', status: 'declared', 
    marks: '', cutOff: '', rank: '', qualified: true, merit: ''
  });

  const [newAnswerKeyForm, setNewAnswerKeyForm] = useState({
    examName: '', department: '', examDate: '', releaseDate: '', 
    objectionDeadline: '', subjects: '', status: 'released', objectionFee: ''
  });

  const user = { 
    name: 'Priya Sharma', 
    email: 'priya.sharma@email.com', 
    education: 'B.Tech Computer Science',
    location: 'Delhi',
    experience: '2 years',
    avatar: '👩‍💻' 
  };

  // ✅ ALL CALLBACK HOOKS MOVED TO TOP LEVEL - NO CONDITIONAL HOOKS
  const calculateDaysRemaining = useCallback((lastDate) => {
    if (!lastDate) return null;
    
    const today = new Date();
    const currentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    const deadline = new Date(lastDate);
    const deadlineDate = new Date(deadline.getFullYear(), deadline.getMonth(), deadline.getDate());
    
    const timeDiff = deadlineDate.getTime() - currentDate.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));
    
    return daysDiff;
  }, []);

  const getExpirationStatus = useCallback((daysRemaining) => {
    if (daysRemaining === null) return null;
    
    if (daysRemaining < 0) {
      return {
        text: 'Form Filling Date Expired',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        iconColor: 'text-red-500',
        borderColor: 'border-red-500',
        isExpired: true
      };
    } else if (daysRemaining === 0) {
      return {
        text: 'Last Day to Apply Today!',
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-800',
        iconColor: 'text-orange-500',
        borderColor: 'border-orange-500',
        isExpired: false
      };
    } else if (daysRemaining === 1) {
      return {
        text: 'Only 1 Day Left to Apply',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        iconColor: 'text-red-500',
        borderColor: 'border-red-500',
        isExpired: false
      };
    } else if (daysRemaining <= 7) {
      return {
        text: `Only ${daysRemaining} Days Left to Apply`,
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        iconColor: 'text-yellow-500',
        borderColor: 'border-yellow-500',
        isExpired: false
      };
    } else if (daysRemaining <= 30) {
      return {
        text: `${daysRemaining} Days Remaining to Fill Form`,
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        iconColor: 'text-blue-500',
        borderColor: 'border-blue-500',
        isExpired: false
      };
    } else {
      return {
        text: `${daysRemaining} Days Remaining to Fill Form`,
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        iconColor: 'text-green-500',
        borderColor: 'border-green-500',
        isExpired: false
      };
    }
  }, []);

  const getJobCategories = useCallback(() => {
    const safeJobs = jobPosts.filter(job => {
      const days = calculateDaysRemaining(job.lastDate);
      return days > 7;
    });

    const urgentJobs = jobPosts.filter(job => {
      const days = calculateDaysRemaining(job.lastDate);
      return days >= 0 && days <= 7;
    });

    const expiredJobs = jobPosts.filter(job => {
      const days = calculateDaysRemaining(job.lastDate);
      return days < 0;
    });

    const recentJobs = jobPosts.filter(job => {
      const postDate = new Date(job.postDate);
      const today = new Date();
      const daysSincePost = Math.floor((today - postDate) / (1000 * 3600 * 24));
      return daysSincePost <= 20;
    });

    return {
      safe: safeJobs,
      urgent: urgentJobs,
      expired: expiredJobs,
      recent: recentJobs,
      all: jobPosts.filter(job => job.status === 'Active')
    };
  }, [jobPosts, calculateDaysRemaining]);

  const getFilteredAndSortedJobs = useCallback(() => {
    const categories = getJobCategories();
    let filteredJobs = [];

    switch (activeFilter) {
      case 'safe':
        filteredJobs = categories.safe;
        break;
      case 'urgent':
        filteredJobs = categories.urgent;
        break;
      case 'expired':
        filteredJobs = categories.expired;
        break;
      case 'recent':
        filteredJobs = categories.recent;
        break;
      default:
        filteredJobs = categories.all;
    }

    return filteredJobs.sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          const tsA = new Date(a.createdAt || a.postDate || 0).getTime();
          const tsB = new Date(b.createdAt || b.postDate || 0).getTime();
          return tsB - tsA;
        case 'deadline':
          const daysA = calculateDaysRemaining(a.lastDate) || -999;
          const daysB = calculateDaysRemaining(b.lastDate) || -999;
          return daysA - daysB;
        case 'priority':
          const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
          return (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
        default:
          return 0;
      }
    });
  }, [activeFilter, sortBy, getJobCategories, calculateDaysRemaining]);

  // Section lists - filter by showInTiles OR showInTicker when admin has selected some; otherwise show all. Sorted by tileOrder then created_at.
  const getCreatedTs = (item) => (item?.createdAt || item?.created_at || item?.postDate) ? new Date(item.createdAt || item.created_at || item.postDate).getTime() : 0;
  const filterByTiles = (list, hasTileKey = 'showInTiles', tickerKey = 'showInTicker', orderKey = 'tileOrder') => {
    const selected = list.filter((x) => x[hasTileKey] || x[tickerKey]);
    if (selected.length > 0) {
      return [...selected].sort((a, b) => (a[orderKey] || 999) - (b[orderKey] || 999));
    }
    return [...list].sort((a, b) => getCreatedTs(b) - getCreatedTs(a));
  };
  const sectionResults = React.useMemo(() => filterByTiles(results), [results]);
  const sectionAdmitCards = React.useMemo(() => filterByTiles(admitCards), [admitCards]);
  const sectionJobPosts = React.useMemo(() => filterByTiles(jobPosts), [jobPosts]);
  const sectionAnswerKeys = React.useMemo(() => filterByTiles(answerKeys), [answerKeys]);
  const sectionAdmissions = React.useMemo(() => filterByTiles(admissions), [admissions]);

  // Latest Jobs: by default show ALL jobs (newest first). Tile/ticker only for homepage highlights.
  const latestJobsList = React.useMemo(() => {
    const active = (jobPosts || []).filter((j) => (j.status || '').toLowerCase() === 'active');
    return [...active].sort((a, b) => (getCreatedTs(b) - getCreatedTs(a)));
  }, [jobPosts]);

  const getFilteredAndSortedResults = useCallback(() => {
    const q = (resultSearch || '').toLowerCase().trim();
    let list = sectionResults;
    if (q) list = list.filter((r) => (r.examName || '').toLowerCase().includes(q) || (r.department || '').toLowerCase().includes(q));
    return [...list].sort((a, b) => {
      if (resultSortBy === 'recent') {
        const tsA = new Date(a.createdAt || a.created_at || 0).getTime();
        const tsB = new Date(b.createdAt || b.created_at || 0).getTime();
        return tsB - tsA;
      }
      if (resultSortBy === 'dept') return (a.department || '').localeCompare(b.department || '');
      if (resultSortBy === 'date') return new Date(b.resultDate || 0).getTime() - new Date(a.resultDate || 0).getTime();
      return 0;
    });
  }, [sectionResults, resultSearch, resultSortBy]);

  const getFilteredAndSortedAdmitCards = useCallback(() => {
    const q = (admitSearch || '').toLowerCase().trim();
    let list = sectionAdmitCards;
    if (q) list = list.filter((a) => (a.examName || '').toLowerCase().includes(q) || (a.department || '').toLowerCase().includes(q));
    return [...list].sort((a, b) => {
      if (admitSortBy === 'recent') {
        const tsA = new Date(a.createdAt || a.created_at || 0).getTime();
        const tsB = new Date(b.createdAt || b.created_at || 0).getTime();
        return tsB - tsA;
      }
      if (admitSortBy === 'dept') return (a.department || '').localeCompare(b.department || '');
      if (admitSortBy === 'date') return new Date(b.examDate || 0).getTime() - new Date(a.examDate || 0).getTime();
      return 0;
    });
  }, [sectionAdmitCards, admitSearch, admitSortBy]);

  const getFilteredAndSortedAnswerKeys = useCallback(() => {
    const q = (answerSearch || '').toLowerCase().trim();
    let list = sectionAnswerKeys;
    if (q) list = list.filter((k) => (k.examName || '').toLowerCase().includes(q) || (k.department || '').toLowerCase().includes(q));
    return [...list].sort((a, b) => {
      if (answerSortBy === 'recent') {
        const tsA = new Date(a.createdAt || a.created_at || 0).getTime();
        const tsB = new Date(b.createdAt || b.created_at || 0).getTime();
        return tsB - tsA;
      }
      if (answerSortBy === 'dept') return (a.department || '').localeCompare(b.department || '');
      if (answerSortBy === 'date') return new Date(b.examDate || 0).getTime() - new Date(a.examDate || 0).getTime();
      return 0;
    });
  }, [sectionAnswerKeys, answerSearch, answerSortBy]);

  const getFilteredAndSortedAdmissions = useCallback(() => {
    const q = (admissionSearch || '').toLowerCase().trim();
    let list = sectionAdmissions;
    if (q) list = list.filter((a) => (a.examName || '').toLowerCase().includes(q) || (a.department || '').toLowerCase().includes(q));
    return [...list].sort((a, b) => {
      if (admissionSortBy === 'recent') {
        const tsA = new Date(a.createdAt || a.created_at || 0).getTime();
        const tsB = new Date(b.createdAt || b.created_at || 0).getTime();
        return tsB - tsA;
      }
      if (admissionSortBy === 'dept') return (a.department || '').localeCompare(b.department || '');
      if (admissionSortBy === 'date') return new Date(b.examDate || 0).getTime() - new Date(a.examDate || 0).getTime();
      return 0;
    });
  }, [sectionAdmissions, admissionSearch, admissionSortBy]);

  // ✅ MULTIPLE TEMPLATES MANAGEMENT FUNCTIONS
  const getTemplateById = useCallback((templateId) => {
    if (!templateId) return null;
    return professionalTemplates.find(template => template.id === templateId);
  }, [professionalTemplates]);

  const createNewTemplate = useCallback(() => {
    if (!newTemplateName.trim()) {
      alert('Template name is required!');
      return;
    }

    const newTemplate = {
      id: Date.now().toString(),
      name: newTemplateName.trim(),
      description: newTemplateDescription.trim() || 'Custom professional template',
      isDefault: false,
      createdAt: new Date().toISOString(),
      sections: [...DEFAULT_PROFESSIONAL_TEMPLATE.sections]
    };

    setProfessionalTemplates(prev => [...prev, newTemplate]);
    setNewTemplateName('');
    setNewTemplateDescription('');
    setShowAddTemplateForm(false);
    alert(`Template "${newTemplate.name}" created successfully! 🎉`);
  }, [newTemplateName, newTemplateDescription]);

  const duplicateTemplate = useCallback((templateId) => {
    const originalTemplate = getTemplateById(templateId);
    if (!originalTemplate) return;

    const duplicatedTemplate = {
      ...originalTemplate,
      id: Date.now().toString(),
      name: `${originalTemplate.name} (Copy)`,
      description: `Copy of ${originalTemplate.description}`,
      isDefault: false,
      createdAt: new Date().toISOString()
    };

    setProfessionalTemplates(prev => [...prev, duplicatedTemplate]);
    alert(`Template duplicated as "${duplicatedTemplate.name}"! 🎉`);
  }, [getTemplateById]);

  const deleteTemplate = useCallback((templateId) => {
    const template = getTemplateById(templateId);
    if (!template) return;

    if (template.isDefault) {
      alert('Cannot delete the default template!');
      return;
    }

    const jobsUsingTemplate = jobPosts.filter(job => job.selectedTemplateId === templateId);
    if (jobsUsingTemplate.length > 0) {
      alert(`Cannot delete template "${template.name}" as it is being used by ${jobsUsingTemplate.length} job(s). Please change the template for those jobs first.`);
      return;
    }

    if (window.confirm(`Are you sure you want to delete template "${template.name}"?`)) {
      setProfessionalTemplates(prev => prev.filter(t => t.id !== templateId));
      alert(`Template "${template.name}" deleted successfully! 🗑️`);
    }
  }, [getTemplateById, jobPosts]);

  const updateTemplate = useCallback((templateId, updatedSections) => {
    setProfessionalTemplates(prev => prev.map(template => 
      template.id === templateId 
        ? { ...template, sections: updatedSections }
        : template
    ));
  }, []);

  const addCustomFieldToSection = useCallback((section) => {
    if (!newField.name.trim()) {
      alert('Field name is required!');
      return;
    }
    
    const fieldId = newField.name.toLowerCase().replace(/\s+/g, '_');
    const field = {
      ...newField,
      id: fieldId,
      options: newField.type === 'select' ? newField.options.split(',').map(opt => opt.trim()) : []
    };
    
    setCustomFields(prev => ({
      ...prev,
      [section]: [...(prev[section] || []), field]
    }));
    
    setNewField(() => ({ id: '', name: '', type: 'text', options: '', required: false }));
    alert(`Custom field added to ${section}! 🎉`);
  }, [newField]);

  const removeCustomFieldFromSection = useCallback((section, fieldId) => {
    setCustomFields(prev => ({
      ...prev,
      [section]: prev[section].filter(f => f.id !== fieldId)
    }));
    alert(`Field removed from ${section}! 🗑️`);
  }, []);

  const handleAdminLogin = useCallback(() => {
    if (adminPassword === 'Puja@110091') {
      setIsAdminAuthenticated(true);
      setAdminPassword('');
      alert('Welcome to Admin Panel with Multiple Templates Support! 🎉');
    } else {
      alert('Incorrect password!');
    }
  }, [adminPassword]);

  const handleAddJob = useCallback(() => {
    if (!newJobForm.title || !newJobForm.department || !newJobForm.lastDate) {
      alert('Please fill all required fields: Title, Department, and Last Date!');
      return;
    }
    
    const newJob = { 
      id: Date.now(), 
      ...newJobForm, 
      postDate: new Date().toISOString().split('T')[0],
      status: 'Active',
      customSections: [],
      templateOverride: null
    };
    
    setJobPosts(prev => [newJob, ...prev]);
    resetJobForm();
    
    const selectedTemplate = getTemplateById(newJobForm.selectedTemplateId);
    const templateName = selectedTemplate ? selectedTemplate.name : 'Basic Job Page';
    alert(`Job added successfully using "${templateName}"! 🎉`);
  }, [newJobForm, getTemplateById]);

  const handleEditJob = useCallback((job) => {
    setEditingJob(job);
    setNewJobForm(job);
    setShowAddJobForm(true);
  }, []);

  const handleUpdateJob = useCallback(() => {
    setJobPosts(prev => prev.map(job => 
      job.id === editingJob.id ? { ...newJobForm, id: editingJob.id } : job
    ));
    resetJobForm();
    alert('Job updated successfully! ✅');
  }, [editingJob, newJobForm]);

  const handleDeleteJob = useCallback((jobId) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      setJobPosts(prev => prev.filter(job => job.id !== jobId));
      alert('Job deleted successfully! 🗑️');
    }
  }, []);

  const resetJobForm = useCallback(() => {
    setNewJobForm({
      title: '', department: '', location: '', 
      notificationDate: '', applicationStart: '', lastDate: '',
      feePaymentDate: '', correctionDate: '', admitCard: '',
      examDate: '', resultDate: '', genOBCEWSFees: '', scSTFees: '',
      minimumAge: '', maximumAge: '', totalPosts: '',
      eligibilityCriteria: '', salary: '', applicationLink: '',
      urgency: 'medium', isNew: true, status: 'Active',
      selectedTemplateId: null,
      officialWebsite: '',
      roVacancy: '', rmVacancy: '',
      maleHeight: '', femaleHeight: '', maleChest: '', femaleChest: '',
      maleRunning: '', femaleRunning: '', maleLongJump: '', femaleLongJump: '',
      maleHighJump: '', femaleHighJump: ''
    });
    setEditingJob(null);
    setShowAddJobForm(false);
  }, []);

  const handleApplyNow = useCallback((job) => {
    if (job.applicationLink && job.applicationLink.trim()) {
      let url = job.applicationLink.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
      alert(`Opening application page for ${job.title}! 🚀`);
    } else {
      alert(`Application link not available for ${job.title}. Please check the official website.`);
    }
  }, []);

  const processTemplateFields = useCallback((content, jobData) => {
    let processedContent = content;
    
    Object.keys(jobData).forEach(key => {
      const placeholder = new RegExp(`{{${key}}}`, 'g');
      let value = jobData[key];
      
      if (key.includes('Date') && value && value !== 'Notify Later' && value !== 'As Per Schedule') {
        try {
          value = new Date(value).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'long', 
            year: 'numeric'
          });
        } catch (e) {
          // Keep original value if date parsing fails
        }
      }
      
      processedContent = processedContent.replace(placeholder, value || 'To be announced');
    });
    
    return processedContent;
  }, []);
  // ✅ MULTIPLE TEMPLATES MANAGEMENT COMPONENT - ALL HOOKS AT TOP LEVEL
  const MultipleTemplatesManager = React.memo(() => {
    const [activeTemplateId, setActiveTemplateId] = useState(null);
    const [localTemplates, setLocalTemplates] = useState(professionalTemplates);

    // ✅ ALL CALLBACK HOOKS AT TOP LEVEL - NO CONDITIONAL HOOKS
    const saveAllTemplates = useCallback(() => {
      setProfessionalTemplates(localTemplates);
      alert('All templates saved successfully! 🎉');
    }, [localTemplates, setProfessionalTemplates]);

    const updateTemplateSection = useCallback((templateId, sectionId, updatedContent) => {
      setLocalTemplates(prev => prev.map(template => 
        template.id === templateId 
          ? {
              ...template,
              sections: template.sections.map(section => 
                section.id === sectionId 
                  ? { ...section, content: { ...section.content, ...updatedContent } }
                  : section
              )
            }
          : template
      ));
    }, []);

    const toggleSectionRequired = useCallback((templateId, sectionId) => {
      setLocalTemplates(prev => prev.map(template => 
        template.id === templateId 
          ? {
              ...template,
              sections: template.sections.map(section => 
                section.id === sectionId 
                  ? { ...section, isRequired: !section.isRequired }
                  : section
              )
            }
          : template
      ));
    }, []);

    const moveSectionOrder = useCallback((templateId, sectionId, direction) => {
      setLocalTemplates(prev => prev.map(template => 
        template.id === templateId 
          ? {
              ...template,
              sections: (() => {
                const newSections = [...template.sections];
                const sectionIndex = newSections.findIndex(s => s.id === sectionId);
                
                if (direction === 'up' && sectionIndex > 0) {
                  [newSections[sectionIndex], newSections[sectionIndex - 1]] = [newSections[sectionIndex - 1], newSections[sectionIndex]];
                  newSections[sectionIndex].order = sectionIndex + 1;
                  newSections[sectionIndex - 1].order = sectionIndex;
                } else if (direction === 'down' && sectionIndex < newSections.length - 1) {
                  [newSections[sectionIndex], newSections[sectionIndex + 1]] = [newSections[sectionIndex + 1], newSections[sectionIndex]];
                  newSections[sectionIndex].order = sectionIndex + 1;
                  newSections[sectionIndex + 1].order = sectionIndex + 2;
                }
                
                return newSections;
              })()
            }
          : template
      ));
    }, []);

    // ✅ Sync with global state
    useEffect(() => {
      setLocalTemplates(professionalTemplates);
    }, [professionalTemplates]);

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              🎨 Multiple Templates Manager ({localTemplates.length} templates) ⚡
            </h3>
            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Create and manage multiple professional job portal templates
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowAddTemplateForm(true)}
              className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <FolderPlus className="w-4 h-4" />
              Create New Template
            </button>
            <button
              onClick={saveAllTemplates}
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save All Templates
            </button>
          </div>
        </div>

        {/* Create New Template Form */}
        {showAddTemplateForm && (
          <div className={`p-6 rounded-xl border ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h4 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                ➕ Create New Professional Template
              </h4>
              <button
                onClick={() => {
                  setShowAddTemplateForm(false);
                  setNewTemplateName('');
                  setNewTemplateDescription('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Template Name * ⚡
                </label>
                <StableInput
                  value={newTemplateName}
                  onChange={setNewTemplateName}
                  placeholder="e.g., Template A, Banking Jobs Template, etc."
                  className={`w-full px-3 py-2 border rounded-lg ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Description ⚡
                </label>
                <StableInput
                  value={newTemplateDescription}
                  onChange={setNewTemplateDescription}
                  placeholder="Brief description of this template"
                  className={`w-full px-3 py-2 border rounded-lg ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={createNewTemplate}
                className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-lg font-medium transition-colors"
              >
                Create Template ⚡
              </button>
              <button
                onClick={() => {
                  setShowAddTemplateForm(false);
                  setNewTemplateName('');
                  setNewTemplateDescription('');
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-6 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Templates Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {localTemplates.map((template) => {
            const jobsUsingTemplate = jobPosts.filter(job => job.selectedTemplateId === template.id);
            
            return (
              <div key={template.id} className={`p-4 border rounded-lg transition-all hover:shadow-md ${
                activeTemplateId === template.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'
              }`}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h4 className={`font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {template.name}
                    </h4>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {template.description}
                    </p>
                  </div>
                  
                  <div className="flex gap-1">
                    {template.isDefault && (
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold">
                        DEFAULT
                      </span>
                    )}
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">
                      {template.sections.length} SECTIONS
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    📅 Created: {new Date(template.createdAt).toLocaleDateString()}
                  </span>
                  {jobsUsingTemplate.length > 0 && (
                    <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-semibold">
                      {jobsUsingTemplate.length} JOBS USING
                    </span>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveTemplateId(activeTemplateId === template.id ? null : template.id)}
                    className={`flex-1 py-2 px-3 rounded text-sm font-medium transition-colors ${
                      activeTemplateId === template.id
                        ? 'bg-blue-600 text-white'
                        : darkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {activeTemplateId === template.id ? 'Hide Editor' : 'Edit Template'}
                  </button>
                  
                  <button
                    onClick={() => duplicateTemplate(template.id)}
                    className="p-2 text-green-600 hover:bg-green-100 rounded transition-colors"
                    title="Duplicate Template"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  
                  {!template.isDefault && (
                    <button
                      onClick={() => deleteTemplate(template.id)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                      title="Delete Template"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Template Editor */}
        {activeTemplateId && (
          <div className={`p-6 rounded-xl border ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {(() => {
              const activeTemplate = localTemplates.find(t => t.id === activeTemplateId);
              if (!activeTemplate) return null;

              return (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h4 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      📝 Editing: {activeTemplate.name}
                    </h4>
                    <button
                      onClick={() => setActiveTemplateId(null)}
                      className={`px-4 py-2 rounded-lg border ${
                        darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Close Editor
                    </button>
                  </div>

                  {/* Template Sections Editor */}
                  <div className="space-y-4">
                    {activeTemplate.sections.map((section, index) => (
                      <div key={section.id} className={`border rounded-lg ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        {/* Section Header */}
                        <div className="p-4 border-b flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <span className={`text-lg ${
                                section.isRequired ? 'text-red-500' : 'text-green-500'
                              }`}>
                                {section.isRequired ? '🔒' : '📝'}
                              </span>
                              <h4 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {section.title}
                              </h4>
                            </div>
                            
                            <div className="flex gap-2">
                              {section.isRequired ? (
                                <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-semibold">
                                  REQUIRED
                                </span>
                              ) : (
                                <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
                                  OPTIONAL
                                </span>
                              )}
                              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
                                {section.type.toUpperCase()}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {/* Move buttons */}
                            <button
                              onClick={() => moveSectionOrder(activeTemplateId, section.id, 'up')}
                              disabled={index === 0}
                              className={`p-2 rounded-lg transition-colors ${
                                index === 0 
                                  ? 'text-gray-400 cursor-not-allowed' 
                                  : 'text-blue-600 hover:bg-blue-100'
                              }`}
                            >
                              <ArrowUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => moveSectionOrder(activeTemplateId, section.id, 'down')}
                              disabled={index === activeTemplate.sections.length - 1}
                              className={`p-2 rounded-lg transition-colors ${
                                index === activeTemplate.sections.length - 1 
                                  ? 'text-gray-400 cursor-not-allowed' 
                                  : 'text-blue-600 hover:bg-blue-100'
                              }`}
                            >
                              <ArrowDown className="w-4 h-4" />
                            </button>

                            {/* Required toggle (only for optional sections) */}
                            {!section.isRequired && (
                              <button
                                onClick={() => toggleSectionRequired(activeTemplateId, section.id)}
                                className="p-2 text-orange-600 hover:bg-orange-100 rounded-lg transition-colors"
                                title="Toggle Required Status"
                              >
                                <Settings className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Section Content Customization */}
                        <div className="p-4">
                          {section.type === 'title-section' && (
                            <div className="space-y-4">
                              <div>
                                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                  Custom Subtitle Text
                                </label>
                                <StableInput
                                  value={section.content.customText || ''}
                                  onChange={(value) => updateTemplateSection(activeTemplateId, section.id, { customText: value })}
                                  placeholder="e.g., Radio Operator, Radio Mechanic Apply Online"
                                  className={`w-full px-3 py-2 border rounded-lg ${
                                    darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                                  }`}
                                />
                              </div>
                              <div className="flex gap-4">
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={section.content.showUpdatedDate}
                                    onChange={(e) => updateTemplateSection(activeTemplateId, section.id, { showUpdatedDate: e.target.checked })}
                                    className="rounded"
                                  />
                                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Show Updated Date
                                  </span>
                                </label>
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={section.content.showOfficialWebsite}
                                    onChange={(e) => updateTemplateSection(activeTemplateId, section.id, { showOfficialWebsite: e.target.checked })}
                                    className="rounded"
                                  />
                                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Show Official Website
                                  </span>
                                </label>
                              </div>
                            </div>
                          )}

                          {section.type === 'summary-cards' && (
                            <div className="space-y-4">
                              <div>
                                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                  Card Layout
                                </label>
                                <StableSelect
                                  value={section.content.cardLayout || '3-column'}
                                  onChange={(value) => updateTemplateSection(activeTemplateId, section.id, { cardLayout: value })}
                                  className={`w-full px-3 py-2 border rounded-lg ${
                                    darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                                  }`}
                                >
                                  <option value="3-column">3 Column Layout</option>
                                  <option value="4-column">4 Column Layout</option>
                                  <option value="2-column">2 Column Layout</option>
                                </StableSelect>
                              </div>
                              <div className="flex gap-4">
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={section.content.showTotalPosts}
                                    onChange={(e) => updateTemplateSection(activeTemplateId, section.id, { showTotalPosts: e.target.checked })}
                                    className="rounded"
                                  />
                                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Show Total Posts
                                  </span>
                                </label>
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={section.content.showApplicationFee}
                                    onChange={(e) => updateTemplateSection(activeTemplateId, section.id, { showApplicationFee: e.target.checked })}
                                    className="rounded"
                                  />
                                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Show Application Fee
                                  </span>
                                </label>
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={section.content.showMaxAge}
                                    onChange={(e) => updateTemplateSection(activeTemplateId, section.id, { showMaxAge: e.target.checked })}
                                    className="rounded"
                                  />
                                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Show Max Age
                                  </span>
                                </label>
                              </div>
                            </div>
                          )}

                          {/* Other section types customization */}
                          {!['title-section', 'summary-cards'].includes(section.type) && (
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Section Title
                                  </label>
                                  <StableInput
                                    value={section.content.sectionTitle || section.title}
                                    onChange={(value) => updateTemplateSection(activeTemplateId, section.id, { sectionTitle: value })}
                                    className={`w-full px-3 py-2 border rounded-lg ${
                                      darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                                    }`}
                                  />
                                </div>
                                <div>
                                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Section Icon
                                  </label>
                                  <StableInput
                                    value={section.content.sectionIcon || ''}
                                    onChange={(value) => updateTemplateSection(activeTemplateId, section.id, { sectionIcon: value })}
                                    placeholder="📋"
                                    className={`w-full px-3 py-2 border rounded-lg ${
                                      darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
                                    }`}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Available Job Data Fields Reference */}
                  <div className={`p-4 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-300'
                  }`}>
                    <details>
                      <summary className={`cursor-pointer font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        📋 Available Job Data Fields (Click to expand)
                      </summary>
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
                        <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                          <strong>Basic Fields:</strong><br />
                          title, department, location, totalPosts, salary
                        </div>
                        <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                          <strong>Dates:</strong><br />
                          notificationDate, applicationStart, lastDate, feePaymentDate, examDate, resultDate
                        </div>
                        <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                          <strong>Fees & Age:</strong><br />
                          genOBCEWSFees, scSTFees, minimumAge, maximumAge
                        </div>
                        <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                          <strong>Physical Standards:</strong><br />
                          maleHeight, femaleHeight, maleChest, femaleChest, maleRunning, femaleRunning
                        </div>
                      </div>
                    </details>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  });

  // ✅ ENHANCED JOB FORM WITH TEMPLATE SELECTION SUPPORT - ALL HOOKS AT TOP LEVEL
  const EnhancedJobFormWithTemplateSelection = React.memo(({ formData, onFormChange }) => {
    const handleFieldChange = useCallback((field, value) => {
      onFormChange(prev => ({ ...prev, [field]: value }));
    }, [onFormChange]);

    const selectedTemplate = getTemplateById(formData.selectedTemplateId);

    return (
      <div className="space-y-6">
        {/* Template Selection Section */}
        <div className={`p-4 rounded-lg border ${
          darkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'
        }`}>
          <h4 className={`font-semibold mb-3 ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>
            🎨 Template Selection for Job Details Page
          </h4>
          
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Choose Template for "View Details" Page ⚡
            </label>
            <StableSelect
              value={formData.selectedTemplateId || ''}
              onChange={(value) => handleFieldChange('selectedTemplateId', value || null)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
            >
              <option value="">📄 Basic Job Details Page (Default)</option>
              {professionalTemplates.map(template => (
                <option key={template.id} value={template.id}>
                  🎨 {template.name} ({template.sections.length} sections)
                </option>
              ))}
            </StableSelect>
          </div>

          {/* Template Preview Info */}
          {selectedTemplate && (
            <div className={`mt-3 p-3 rounded-lg ${
              darkMode ? 'bg-purple-800/20' : 'bg-purple-100'
            }`}>
              <h5 className={`font-medium mb-2 ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>
                📋 Selected Template: {selectedTemplate.name}
              </h5>
              <p className={`text-sm mb-2 ${darkMode ? 'text-purple-200' : 'text-purple-700'}`}>
                {selectedTemplate.description}
              </p>
              <div className="flex flex-wrap gap-1">
                {selectedTemplate.sections.slice(0, 5).map(section => (
                  <span key={section.id} className="bg-purple-200 text-purple-800 px-2 py-1 rounded text-xs">
                    {section.title}
                  </span>
                ))}
                {selectedTemplate.sections.length > 5 && (
                  <span className="bg-purple-200 text-purple-800 px-2 py-1 rounded text-xs">
                    +{selectedTemplate.sections.length - 5} more
                  </span>
                )}
              </div>
            </div>
          )}

          <div className={`mt-3 p-3 rounded-lg ${
            darkMode ? 'bg-blue-900/20' : 'bg-blue-50'
          }`}>
            <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
              💡 <strong>How it works:</strong><br />
              • <strong>Job Tiles:</strong> Always show basic info (title, department, dates)<br />
              • <strong>View Details:</strong> Shows selected template page with complete professional layout<br />
              • <strong>No Template:</strong> Shows simple job details page
            </p>
          </div>
        </div>

        {/* Basic Job Information - Always Required */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Job Title * ⚡
            </label>
            <StableInput
              value={formData.title || ''}
              onChange={(value) => handleFieldChange('title', value)}
              placeholder="e.g., BSF Head Constable RO/ RM Recruitment 2025"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
            />
          </div>
          
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Department * ⚡
            </label>
            <StableInput
              value={formData.department || ''}
              onChange={(value) => handleFieldChange('department', value)}
              placeholder="e.g., Border Security Force (BSF)"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
            />
          </div>
          
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Location ⚡
            </label>
            <StableInput
              value={formData.location || ''}
              onChange={(value) => handleFieldChange('location', value)}
              placeholder="e.g., All India"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 text-red-600`}>
              ⏰ Last Date Apply Online * ⚡
            </label>
            <StableInput
              type="date"
              value={formData.lastDate || ''}
              onChange={(value) => handleFieldChange('lastDate', value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 border-red-300`}
            />
          </div>
        </div>

        {/* Template-Specific Fields - Show when template is selected */}
        {selectedTemplate && (
          <div className={`p-4 rounded-lg border ${
            darkMode ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'
          }`}>
            <h4 className={`font-semibold mb-4 ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
              🎯 Template-Specific Fields for "{selectedTemplate.name}"
            </h4>
            
            {/* Date Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Notification Date ⚡
                </label>
                <StableInput
                  type="date"
                  value={formData.notificationDate || ''}
                  onChange={(value) => handleFieldChange('notificationDate', value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Application Start Date ⚡
                </label>
                <StableInput
                  type="date"
                  value={formData.applicationStart || ''}
                  onChange={(value) => handleFieldChange('applicationStart', value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Fee Payment Date ⚡
                </label>
                <StableInput
                  type="date"
                  value={formData.feePaymentDate || ''}
                  onChange={(value) => handleFieldChange('feePaymentDate', value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Exam Date ⚡
                </label>
                <StableInput
                  type="date"
                  value={formData.examDate || ''}
                  onChange={(value) => handleFieldChange('examDate', value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
            </div>

            {/* Fee and Age Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Gen/OBC/EWS Fee ⚡
                </label>
                <StableInput
                  value={formData.genOBCEWSFees || ''}
                  onChange={(value) => handleFieldChange('genOBCEWSFees', value)}
                  placeholder="e.g., ₹100"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  SC/ST Fee ⚡
                </label>
                <StableInput
                  value={formData.scSTFees || ''}
                  onChange={(value) => handleFieldChange('scSTFees', value)}
                  placeholder="e.g., ₹00 (Free)"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Minimum Age ⚡
                </label>
                <StableInput
                  value={formData.minimumAge || ''}
                  onChange={(value) => handleFieldChange('minimumAge', value)}
                  placeholder="e.g., 18"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Maximum Age ⚡
                </label>
                <StableInput
                  value={formData.maximumAge || ''}
                  onChange={(value) => handleFieldChange('maximumAge', value)}
                  placeholder="e.g., 25 (General), 28 (OBC), 30 (SC/ST)"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
            </div>

            {/* Job Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Total Posts ⚡
                </label>
                <StableInput
                  value={formData.totalPosts || ''}
                  onChange={(value) => handleFieldChange('totalPosts', value)}
                  placeholder="e.g., 1121"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Salary ⚡
                </label>
                <StableInput
                  value={formData.salary || ''}
                  onChange={(value) => handleFieldChange('salary', value)}
                  placeholder="e.g., ₹21,700 - ₹69,100 (Level 4)"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Application Link * ⚡
                </label>
                <StableInput
                  type="url"
                  value={formData.applicationLink || ''}
                  onChange={(value) => handleFieldChange('applicationLink', value)}
                  placeholder="e.g., https://rectt.bsf.gov.in"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Official Website ⚡
                </label>
                <StableInput
                  value={formData.officialWebsite || ''}
                  onChange={(value) => handleFieldChange('officialWebsite', value)}
                  placeholder="e.g., rectt.bsf.gov.in"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>
            </div>

            {/* Post Breakdown - if template has post breakdown sections */}
            {selectedTemplate.sections.some(s => s.id === 'total-posts-section') && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Radio Operator (RO) Posts ⚡
                  </label>
                  <StableInput
                    value={formData.roVacancy || ''}
                    onChange={(value) => handleFieldChange('roVacancy', value)}
                    placeholder="e.g., 910"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Radio Mechanic (RM) Posts ⚡
                  </label>
                  <StableInput
                    value={formData.rmVacancy || ''}
                    onChange={(value) => handleFieldChange('rmVacancy', value)}
                    placeholder="e.g., 211"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Eligibility Criteria - Always Show */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Eligibility Criteria ⚡
          </label>
          <StableTextarea
            value={formData.eligibilityCriteria || ''}
            onChange={(value) => handleFieldChange('eligibilityCriteria', value)}
            placeholder="Educational qualification, experience requirements..."
            rows={3}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
            }`}
          />
        </div>
        
        {/* Custom Fields */}
        {customFields.jobs?.map((field) => (
          <div key={field.id}>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {field.name} ⚡
            </label>
            {field.type === 'select' ? (
              <StableSelect
                value={formData[field.id] || ''}
                onChange={(value) => handleFieldChange(field.id, value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              >
                <option value="">Select {field.name}</option>
                {field.options?.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </StableSelect>
            ) : field.type === 'textarea' ? (
              <StableTextarea
                value={formData[field.id] || ''}
                onChange={(value) => handleFieldChange(field.id, value)}
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            ) : (
              <StableInput
                type={field.type}
                value={formData[field.id] || ''}
                onChange={(value) => handleFieldChange(field.id, value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
            )}
          </div>
        ))}
      </div>
    );
  });

  // ✅ ADMIN LOGIN COMPONENT - ALL HOOKS AT TOP LEVEL
  const AdminLogin = React.memo(() => {
    const handlePasswordChange = useCallback((value) => {
      setAdminPassword(value);
    }, []);

    const handleKeyPress = useCallback((e) => {
      if (e.key === 'Enter') {
        handleAdminLogin();
      }
    }, [handleAdminLogin]);

    return (
      <div className={`max-w-md mx-auto p-6 rounded-xl border ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="text-center mb-6">
          <Settings className="w-12 h-12 text-blue-500 mx-auto mb-4" />
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Admin Login ⚡
          </h2>
          <p className={`text-sm mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Access Multiple Templates Manager!
          </p>
        </div>
        <div className="space-y-4">
          <StableInput
            type="password"
            value={adminPassword}
            onChange={handlePasswordChange}
            onKeyPress={handleKeyPress}
            placeholder="Enter admin password"
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
            }`}
            autoFocus={true}
          />
          <button
            onClick={handleAdminLogin}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
          >
            Login to Admin Panel ⚡
          </button>
          {/*
          <p className={`text-xs text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Demo password: admin123
          </p>
          */}
        </div>
      </div>
    );
  });

  // ✅ ADD CUSTOM FIELD FORM - ALL HOOKS AT TOP LEVEL
  const AddCustomFieldForm = React.memo(() => {
    const handleNameChange = useCallback((value) => {
      setNewField(prev => ({ ...prev, name: value }));
    }, []);

    const handleTypeChange = useCallback((value) => {
      setNewField(prev => ({ ...prev, type: value }));
    }, []);

    const handleOptionsChange = useCallback((value) => {
      setNewField(prev => ({ ...prev, options: value }));
    }, []);

    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <StableInput
          value={newField.name}
          onChange={handleNameChange}
          placeholder="Field Name"
          className={`px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
            darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
          }`}
        />
        
        <StableSelect
          value={newField.type}
          onChange={handleTypeChange}
          className={`px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
            darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
          }`}
        >
          <option value="text">Text</option>
          <option value="number">Number</option>
          <option value="date">Date</option>
          <option value="select">Dropdown</option>
          <option value="textarea">Text Area</option>
        </StableSelect>
        
        {newField.type === 'select' && (
          <StableInput
            value={newField.options}
            onChange={handleOptionsChange}
            placeholder="Options (comma separated)"
            className={`px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
              darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'
            }`}
          />
        )}
        
        <button
          onClick={() => addCustomFieldToSection('jobs')}
          className="bg-green-600 hover:bg-green-700 text-white py-2 px-3 rounded-lg flex items-center justify-center gap-1 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Field ⚡
        </button>
      </div>
    );
  });
  // ✅ PROFESSIONAL PAGE RENDERER COMPONENT - ALL HOOKS AT TOP LEVEL
  const ProfessionalPageRenderer = React.memo(({ job, darkMode }) => {
    const template = getTemplateById(job.selectedTemplateId);
    const daysRemaining = calculateDaysRemaining(job.lastDate);
    const expirationStatus = getExpirationStatus(daysRemaining);

    // ✅ ALL CALLBACK HOOKS AT TOP LEVEL - NO CONDITIONAL HOOKS
    const renderSection = useCallback((section) => {
      const processedContent = section.content;

      switch (section.type) {
        case 'title-section':
          return (
            <div className={`p-8 rounded-xl border mb-8 text-center ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <h1 className={`text-4xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {job.title}
              </h1>
              {processedContent.customText && (
                <h2 className={`text-2xl font-semibold mb-2 text-blue-600`}>
                  {processedContent.customText}
                </h2>
              )}
              {processedContent.showUpdatedDate && job.updatedAt && (
                <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Last Updated on: {job.updatedAt || new Date().toLocaleDateString('en-IN')}
                </p>
              )}
              {processedContent.showOfficialWebsite && job.officialWebsite && (
                <div className="mt-4">
                  <a 
                    href={`https://${job.officialWebsite}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 hover:text-blue-800 underline font-semibold"
                  >
                    {job.officialWebsite} - {job.department}
                  </a>
                </div>
              )}
            </div>
          );

        case 'summary-cards':
          return (
            <div className={`p-6 rounded-xl border mb-8 ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <div className={`grid grid-cols-1 ${
                processedContent.cardLayout === '4-column' ? 'md:grid-cols-4' :
                processedContent.cardLayout === '2-column' ? 'md:grid-cols-2' : 'md:grid-cols-3'
              } gap-6`}>
                {processedContent.showTotalPosts && job.totalPosts && (
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">{job.totalPosts}</div>
                    <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Total Posts</div>
                  </div>
                )}
                {processedContent.showApplicationFee && job.genOBCEWSFees && (
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">{job.genOBCEWSFees}</div>
                    <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Application Fee (Gen/OBC/EWS)</div>
                  </div>
                )}
                {processedContent.showMaxAge && job.maximumAge && (
                  <div className="text-center">
                    <div className="text-3xl font-bold text-orange-600 mb-2">{job.maximumAge.split(',')[0] || job.maximumAge}</div>
                    <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Max Age</div>
                  </div>
                )}
              </div>
            </div>
          );

        case 'important-dates':
          return (
            <div className={`p-6 rounded-xl border mb-8 ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <h2 className="text-2xl font-bold mb-4 text-center text-pink-600">
                {processedContent.sectionIcon} {processedContent.sectionTitle}
              </h2>
              <div className="space-y-3">
                {processedContent.fields?.map((field, index) => {
                  const value = job[field.field];
                  if (!value && !field.required) return null;
                  
                  return (
                    <div key={index} className="flex justify-between">
                      <span className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {field.label}:
                      </span>
                      <span className={`${field.highlight ? 'text-red-600 font-bold' : darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {value ? (field.field.includes('Date') && value !== 'Notify Later' && value !== 'As Per Schedule' ? 
                          new Date(value).toLocaleDateString('en-IN') : value) : 'To be announced'}
                      </span>
                    </div>
                  );
                })}
              </div>
              {processedContent.showWarningNote && (
                <div className={`mt-4 p-3 rounded-lg ${darkMode ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                  <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                    {processedContent.warningText} {job.department}.
                  </p>
                </div>
              )}
            </div>
          );

        case 'application-fee':
          return (
            <div className={`p-6 rounded-xl border mb-8 ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <h2 className="text-2xl font-bold mb-4 text-center text-green-600">
                {processedContent.sectionIcon} {processedContent.sectionTitle}
              </h2>
              <div className="space-y-4">
                {processedContent.feeStructure?.map((fee, index) => (
                  <div key={index} className="flex justify-between">
                    <span className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {fee.category}:
                    </span>
                    <span className="text-green-600 font-bold text-lg">
                      {job[fee.field] || 'To be announced'}
                    </span>
                  </div>
                ))}
              </div>
              {processedContent.showPaymentMethods && processedContent.paymentMethods && (
                <div className={`mt-6 p-4 rounded-lg ${darkMode ? 'bg-green-900/20' : 'bg-green-50'}`}>
                  <h3 className={`font-semibold mb-2 ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
                    Payment Methods:
                  </h3>
                  <ul className={`text-sm space-y-1 ${darkMode ? 'text-green-200' : 'text-green-700'}`}>
                    {processedContent.paymentMethods.map((method, index) => (
                      <li key={index}>• {method}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );

        case 'age-limit':
          return (
            <div className={`p-6 rounded-xl border mb-8 ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <h2 className="text-2xl font-bold mb-4 text-center text-purple-600">
                {processedContent.sectionIcon} {processedContent.sectionTitle} (As on {processedContent.ageAsOnDate})
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Minimum Age:</span>
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{job.minimumAge} Years</span>
                </div>
                <div className="flex justify-between">
                  <span className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Maximum Age:</span>
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{job.maximumAge}</span>
                </div>
              </div>
            </div>
          );

        case 'total-posts':
          return (
            <div className={`p-6 rounded-xl border mb-8 ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <h2 className="text-2xl font-bold mb-4 text-center text-red-600">
                {processedContent.sectionIcon} {processedContent.sectionTitle}
              </h2>
              <div className="text-center mb-4">
                <div className="text-6xl font-bold text-red-600 mb-2">{job.totalPosts}</div>
                <div className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Posts</div>
              </div>
              {processedContent.showPostBreakdown && processedContent.postBreakdown && (
                <div className="space-y-3">
                  {processedContent.postBreakdown.map((post, index) => (
                    job[post.field] && (
                      <div key={index} className="flex justify-between">
                        <span className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {post.postName}:
                        </span>
                        <span className="text-blue-600 font-bold">{job[post.field]} Posts</span>
                      </div>
                    )
                  ))}
                </div>
              )}
            </div>
          );

        case 'eligibility-table':
          return (
            <div className={`p-6 rounded-xl border mb-8 ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <h2 className="text-2xl font-bold mb-6 text-center text-blue-600">
                {processedContent.sectionIcon} {job.title} {processedContent.sectionTitle}
              </h2>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-blue-100">
                      {processedContent.tableColumns?.map((col, index) => (
                        <th key={index} className="border border-gray-300 px-4 py-3 text-left" style={{width: col.width}}>
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {processedContent.eligibilityData?.map((row, index) => (
                      <tr key={index} className={index % 2 === 1 ? 'bg-gray-50' : ''}>
                        <td className="border border-gray-300 px-4 py-3 font-semibold">{row.postName}</td>
                        <td className="border border-gray-300 px-4 py-3 text-center font-bold text-blue-600">
                          {job[row.totalField] || 'TBA'}
                        </td>
                        <td className="border border-gray-300 px-4 py-3 text-sm">{row.criteria}</td>
                      </tr>
                    ))}
                    <tr className="bg-blue-50">
                      <td className="border border-gray-300 px-4 py-3 font-semibold">Total</td>
                      <td className="border border-gray-300 px-4 py-3 text-center font-bold text-red-600 text-lg">
                        {job.totalPosts}
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-sm italic">
                        For more complete information kindly read the {job.title} Recruitment Exam Notification 2025.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );

        case 'salary-table':
          return (
            <div className={`p-6 rounded-xl border mb-8 ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <h2 className="text-2xl font-bold mb-6 text-center text-green-600">
                {processedContent.sectionIcon} {job.title} {processedContent.sectionTitle}
              </h2>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-green-100">
                      <th className="border border-gray-300 px-4 py-3 text-left">Allowance</th>
                      <th className="border border-gray-300 px-4 py-3 text-left">Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {processedContent.salaryData?.map((salary, index) => (
                      <tr key={index} className={index % 2 === 1 ? 'bg-gray-50' : ''}>
                        <td className="border border-gray-300 px-4 py-3 font-semibold">{salary.label}</td>
                        <td className="border border-gray-300 px-4 py-3 text-green-600 font-bold">
                          {salary.field ? `${job[salary.field]} ${salary.suffix || ''}` : salary.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );

        default:
          return null;
      }
    }, [job, darkMode]);

    // ✅ EARLY RETURN AFTER ALL HOOKS
    if (!template) return null;

    return (
      <div className="space-y-6">
        {template.sections
          .filter(section => section.isRequired || section.isEnabled !== false)
          .sort((a, b) => a.order - b.order)
          .map(section => (
            <div key={section.id}>
              {renderSection(section)}
            </div>
          ))}
      </div>
    );
  });

  // Related Jobs block - automated internal linking for SEO
  const RelatedJobsBlock = ({ currentJobId, department, darkMode }) => {
    const [related, setRelated] = useState([]);
    useEffect(() => {
      fetchJobs().then((res) => {
        if (!res.success || !res.data) return;
        const list = (res.data || [])
          .filter((j) => j.id !== currentJobId && j.status !== 'Inactive')
          .slice(0, 20);
        const dept = (department || '').toLowerCase();
        const sameDept = dept ? list.filter((j) => String(j.department || '').toLowerCase().includes(dept) || dept.includes(String(j.department || '').toLowerCase())) : [];
        const rest = list.filter((j) => !sameDept.includes(j));
        setRelated([...sameDept, ...rest].slice(0, 5));
      });
    }, [currentJobId, department]);
    if (related.length === 0) return null;
    return (
      <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="bg-slate-100 dark:bg-slate-700 px-4 py-2 font-bold text-slate-900 dark:text-white text-sm sm:text-base">Related Sarkari Naukri / Jobs</div>
        <ul className="list-disc list-inside p-4 space-y-1 text-sm">
          {related.map((j) => (
            <li key={j.id}>
              <RouterLink
                to={`/job/${j.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`font-medium hover:underline ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}
              >
                {j.title || j.department}
              </RouterLink>
              {j.department && <span className={darkMode ? 'text-gray-500' : 'text-gray-500'}> – {j.department}</span>}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // ✅ JOB DETAIL PAGE - Table layout (left=field, right=details) like SarkariResult Latest Job PDF
  const JobDetailPage = () => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
      const load = async () => {
        if (!jobId) return;
        setLoading(true);
        setError(null);
        const res = await fetchJobById(parseInt(jobId));
        setLoading(false);
        if (res.success && res.data) setJob(res.data);
        else setError('Job not found');
      };
      load();
    }, [jobId]);

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null;
    const fmtFee = (v) => v ? (/^[\d]+$/.test(String(v)) ? `₹${v}` : v) : null;
    const lastDate = job?.applicationLast || job?.application_last;
    const daysRemaining = lastDate ? Math.ceil((new Date(lastDate) - new Date()) / (1000 * 60 * 60 * 24)) : null;
    const expirationStatus = daysRemaining !== null && daysRemaining < 0 ? { isExpired: true } : null;

    if (loading) {
      return (
        <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sarkari-red"></div>
        </div>
      );
    }
    if (error || !job) {
      return (
        <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          <div className="text-center">
            <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Job Not Found</h2>
            {window.history.length > 2 && (
              <button onClick={() => navigate(-1)} className="bg-sarkari-red text-white px-4 py-2 rounded-lg hover:bg-red-700">Back</button>
            )}
          </div>
        </div>
      );
    }

    const importantDates = [
      { label: 'Application Begin', value: fmtDate(job.applicationStart) },
      { label: 'Last Date for Apply Online', value: fmtDate(job.applicationLast) },
      { label: 'Last Date Pay Exam Fee', value: fmtDate(job.feePaymentDate) },
      { label: 'Form Correction Last Date', value: job.correctionDate },
      { label: 'Exam Start', value: job.examDate },
      { label: 'Answer Key Available', value: job.answerKeyDate },
      { label: 'Admit Card', value: job.admitCardDate },
      { label: 'Result Available', value: job.resultDate },
    ].filter(r => r.value);

    const feeRows = [
      { label: 'General / OBC / EWS', value: fmtFee(job.feeGeneral) },
      { label: 'OBC / SC / ST', value: fmtFee(job.feeScst) },
      { label: 'PH (Divyang)', value: fmtFee(job.feePh) },
      { label: 'Portal Charges', value: fmtFee(job.portalCharges) },
    ].filter(r => r.value);

    const ageRows = [
      { label: 'Minimum Age', value: job.ageMin },
      { label: 'Maximum Age', value: job.ageMax },
      { label: 'Age Relaxation', value: job.ageRelaxation },
    ].filter(r => r.value);

    const isValidUrl = (u) => typeof u === 'string' && u.trim().length > 0 && (u.trim().startsWith('http://') || u.trim().startsWith('https://'));
    const applyUrl = job.applyLink || job.applicationLink;
    const primaryApplyUrlRaw = applyUrl || (job.importantLinks?.[0]?.url) || job.officialWebsite;
    const primaryApplyUrl = primaryApplyUrlRaw && isValidUrl(primaryApplyUrlRaw) ? (primaryApplyUrlRaw.startsWith('http') ? primaryApplyUrlRaw : `https://${primaryApplyUrlRaw}`) : null;
    const linkItems = [
      { label: 'Apply Online', url: applyUrl },
      { label: 'Download Notification', url: job.notificationLink },
      ...(job.importantLinks || []).map(l => ({ label: l.label || l.title || 'Link', url: l.url })),
      { label: 'Official Website', url: job.officialWebsite },
    ].filter(l => l.url && isValidUrl(l.url));

    const jobUrl = `${SITE_URL}/job/${job.id}`;
    const lastDateStr = fmtDate(job.applicationLast) || '';
    const ageStr = job.ageMax ? `Age Limit ${job.ageMax}` : '';
    const seoTitle = `${job.title} – Apply Online, Last Date ${lastDateStr}${ageStr ? `, ${ageStr}` : ''} 2026`;
    const desc = (job.shortDescription || `${job.title} - ${job.department}. Sarkari Naukri, Sarkari Result. Govt Job. Apply before ${lastDateStr || 'see dates'}. Sarkariresult.cloud.`).slice(0, 160);
    const datePosted = job.notificationDate || job.applicationStart || new Date().toISOString().split('T')[0];
    const validThrough = job.applicationLast || undefined;
    const hiringOrg = {
      '@type': 'Organization',
      name: job.department || 'Government',
      ...(job.officialWebsite && { sameAs: job.officialWebsite }),
    };
    const baseSalary = job.salary ? { '@type': 'MonetaryAmount', currency: 'INR', value: { '@type': 'QuantitativeValue', value: job.salary } } : undefined;
    const jobSchema = {
      '@type': 'JobPosting',
      title: job.title,
      description: (job.shortDescription || `${job.title} at ${job.department}. Government Job - Sarkari Naukri. Apply at Sarkariresult.cloud.`).slice(0, 5000),
      datePosted: typeof datePosted === 'string' && datePosted.includes('-') ? datePosted : new Date().toISOString().split('T')[0],
      validThrough: validThrough && typeof validThrough === 'string' && validThrough.includes('-') ? validThrough : undefined,
      hiringOrganization: hiringOrg,
      jobLocation: { '@type': 'Place', address: { '@type': 'PostalAddress', addressCountry: 'IN' } },
      employmentType: 'FULL_TIME',
      url: jobUrl,
      ...(baseSalary && { baseSalary }),
    };
    const jobFaqSchema = {
      '@type': 'FAQPage',
      mainEntity: [
        { '@type': 'Question', name: `Sarkari Naukri ${job.title} - Last Date Kab Hai?`, acceptedAnswer: { '@type': 'Answer', text: `Sarkari Naukri apply last date: ${fmtDate(job.applicationLast) || 'Notification dekhen'}. Sarkariresult.cloud par daily update.` }},
        { '@type': 'Question', name: `Sarkari Result Is Job Ke Liye Kab Aayega?`, acceptedAnswer: { '@type': 'Answer', text: `Sarkari Result date: ${fmtDate(job.resultDate) || 'TBA'}. Sarkari Exam Result Sarkariresult.cloud par check karein.` }},
        { '@type': 'Question', name: 'Sarkari Naukri Kaise Apply Kare?', acceptedAnswer: { '@type': 'Answer', text: `Sarkariresult.cloud par Apply Online link pe click karo. Official website par form bharo. Sarkari Naukri apply karte waqt last date dhyan rakhein.` }},
        { '@type': 'Question', name: `${job.title} Ke Liye Eligibility Kya Hai?`, acceptedAnswer: { '@type': 'Answer', text: `Age limit: ${job.ageMax ? job.ageMax + ' years' : 'Notification dekhen'}. ${job.department || 'Department'} ki official notification mein full eligibility. Sarkariresult.cloud par link milega.` }},
        { '@type': 'Question', name: 'Application Fee Kitni Hai?', acceptedAnswer: { '@type': 'Answer', text: `Fee: ${fmtFee(job.feeGeneral) || 'Notification dekhen'}. SC/ST/PH ke liye alag fee ho sakti hai. Sarkariresult.cloud par full fee details.` }},
      ],
    };
    const jobHowToSchema = {
      '@type': 'HowTo',
      name: 'Sarkari Naukri Kaise Apply Kare - Sarkariresult.cloud',
      description: `Sarkari Naukri ${job.title} apply karne ka tarika. Sarkariresult.cloud par.`,
      step: [
        { '@type': 'HowToStep', name: 'Sarkariresult.cloud kholo', text: 'sarkariresult.cloud par jao, Sarkari Naukri section mein is job ko dhoondo' },
        { '@type': 'HowToStep', name: 'Apply Online pe click karo', text: 'Apply Online button ya official link se form open karo' },
        { '@type': 'HowToStep', name: 'Form bharo', text: 'Official website par Sarkari Naukri form bharo. Last date se pehle submit karo.' },
      ],
    };
    const jobBreadcrumbSchema = {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Latest Jobs', item: `${SITE_URL}/latest-jobs` },
        { '@type': 'ListItem', position: 3, name: job.title?.slice(0, 50) || 'Job', item: jobUrl },
      ],
    };

    return (
      <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <SeoHead title={seoTitle} description={desc} schema={[jobSchema, jobFaqSchema, jobHowToSchema, jobBreadcrumbSchema]} canonical={jobUrl} />
        <div className="max-w-4xl mx-auto px-3 py-4 sm:p-6">
          <div className="mb-4">
            {window.history.length > 2 && (
              <button onClick={() => navigate(-1)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${darkMode ? 'border-gray-600 hover:bg-gray-700 text-white' : 'border-gray-300 hover:bg-gray-100 text-gray-900'}`}>
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
          </div>

          <div className={`rounded-t-lg overflow-hidden border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="bg-sarkari-red text-white px-4 py-4 text-center">
              <div className="text-lg sm:text-xl font-bold uppercase tracking-wide">SARKARIRESULT.CLOUD</div>
              <div className="text-xs sm:text-sm font-medium mt-1 opacity-95">WWW.SARKARIRESULT.CLOUD</div>
            </div>
            <div className="p-4 sm:p-6">
              {(() => {
                const align = job.titleAlign || 'left';
                const alignClass = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : '';
                const size = job.titleSize || 'md';
                const sizeClass = size === 'sm' ? 'text-lg sm:text-xl' : size === 'lg' ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl';
                const colorClass = darkMode ? 'text-white' : 'text-gray-900';
                const weight = job.titleBold === false ? 'font-semibold' : 'font-bold';
                const style = {};
                if (job.titleColor) style.color = job.titleColor;
                return (
                  <h1 className={`${sizeClass} ${weight} mb-2 ${colorClass} ${alignClass}`} style={style}>
                    {job.title}
                  </h1>
                );
              })()}
              {job.postDateUpdate && <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Post Date / Update: {job.postDateUpdate}</p>}
              {job.shortDescription && <p className={`mt-3 text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{job.shortDescription}</p>}
              {job.department && job.department !== 'Not Specified' && (
                <p className={`mt-2 font-semibold text-sarkari-red ${darkMode ? 'text-red-400' : ''}`}>{job.department}</p>
              )}
            </div>
          </div>

          {/* Dynamic tables (from detail_table): merge, bold, align, text/cell colour */}
          {job.detailTable && (() => {
            const toCell = (v) => { const o = typeof v === 'object' && v != null && 'text' in v ? v : {}; return { text: String(o.text ?? v ?? ''), bold: !!o.bold, align: o.align || 'left', textColor: o.textColor || '', fill: o.fill || '', colspan: Math.max(1, parseInt(o.colspan, 10) || 1) }; };
            const toRow = (r, colCount) => {
              if (Array.isArray(r)) { const cells = r.slice(0, colCount).map(toCell); while (cells.length < colCount) cells.push(toCell('')); return { merged: false, cells }; }
              const merged = !!r.merged;
              let cells = Array.isArray(r.cells) ? r.cells.map(toCell) : [toCell('')];
              if (merged) cells = [cells[0] || toCell('')];
              else while (cells.length < colCount) cells.push(toCell(''));
              return { merged, cells };
            };
            const tables = Array.isArray(job.detailTable.tables) ? job.detailTable.tables : null;
            const single = !tables && Array.isArray(job.detailTable.columns) && job.detailTable.columns.length > 0 ? [{ columns: job.detailTable.columns, rows: job.detailTable.rows || [] }] : null;
            const toRender = tables || single;
            if (!toRender || toRender.length === 0) return null;
            return toRender.map((tbl, ti) => {
              const cols = tbl.columns || [];
              const colCount = cols.length || 1;
              const rows = (Array.isArray(tbl.rows) ? tbl.rows : []).map((r) => toRow(r, colCount));
              return (
                <div key={ti} className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className={darkMode ? 'bg-gray-700' : 'bg-green-100'}>
                          {cols.map((col, i) => (
                            <th key={i} className={`p-2 sm:p-3 text-left font-bold border-r last:border-r-0 ${darkMode ? 'text-gray-200 border-gray-600' : 'text-green-900 border-gray-300'}`}>{col || `Column ${i + 1}`}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, ri) => (
                          <tr key={ri} className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                            {row.merged && row.cells?.length === 1 ? (
                              <td colSpan={colCount} className={`p-2 sm:p-3 whitespace-pre-line border-r last:border-r-0 ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-300'}`} style={{ textAlign: row.cells[0].align || 'left', color: row.cells[0].textColor || undefined, backgroundColor: row.cells[0].fill || undefined }}>
                                {row.cells[0].bold ? <strong>{row.cells[0].text || '—'}</strong> : (row.cells[0].text || '—')}
                              </td>
                            ) : (
                              (row.cells || []).map((cell, ci) => (
                                <td key={ci} colSpan={cell.colspan || 1} className={`p-2 sm:p-3 whitespace-pre-line border-r last:border-r-0 ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-300'}`} style={{ textAlign: cell.align || 'left', color: cell.textColor || undefined, backgroundColor: cell.fill || undefined }}>
                                  {cell.bold ? <strong>{cell.text || '—'}</strong> : (cell.text || '—')}
                                </td>
                              ))
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            });
          })()}

          {/* Fallback: static Important Date | Application Fee (when no detail_table tables) */}
          {(!job.detailTable?.tables?.length && !(job.detailTable?.columns?.length)) && (importantDates.length > 0 || feeRows.length > 0) && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200 dark:divide-gray-600">
                <div className="p-4">
                  <div className="font-bold text-green-700 dark:text-green-400 text-sm sm:text-base mb-3">Important Date</div>
                  <ul className="space-y-1.5 text-sm list-disc list-inside">
                    {importantDates.map((r, i) => (
                      <li key={i} className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                        <span className="font-medium">{r.label} : </span>
                        <span className="font-bold">{r.value}</span>
                      </li>
                    ))}
                    {importantDates.length === 0 && <li className={darkMode ? 'text-gray-500' : 'text-gray-500'}>—</li>}
                  </ul>
                </div>
                <div className="p-4">
                  <div className="font-bold text-green-700 dark:text-green-400 text-sm sm:text-base mb-3">Application Fee</div>
                  <ul className="space-y-1.5 text-sm list-disc list-inside">
                    {feeRows.map((r, i) => (
                      <li key={i} className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                        <span className="font-medium">{r.label} : </span>
                        <span className="font-bold">{r.value}</span>
                      </li>
                    ))}
                    {job.paymentMode && (
                      <li className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{job.paymentMode}</li>
                    )}
                    {feeRows.length === 0 && !job.paymentMode && <li className={darkMode ? 'text-gray-500' : 'text-gray-500'}>—</li>}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Fallback: Age Limit (when no detail_table tables) */}
          {(!job.detailTable?.tables?.length && !(job.detailTable?.columns?.length)) && ageRows.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`px-4 py-2 font-bold text-sm sm:text-base border-b ${darkMode ? 'border-gray-600 text-gray-200' : 'border-gray-200 text-gray-800'}`}>
                <span className="text-green-700 dark:text-green-400">{job.title?.slice(0, 50)}{job.title?.length > 50 ? '…' : ''}</span> : <span className="text-purple-700 dark:text-purple-400">Age Limit Details</span>
              </div>
              <ul className="p-4 space-y-1.5 text-sm list-disc list-inside">
                {ageRows.map((r, i) => (
                  <li key={i} className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                    <span className="font-medium">{r.label} : </span>
                    <span className="font-bold">{r.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Vacancy / Total Posts */}
          {job.totalPosts && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-purple-100 px-4 py-2 font-bold text-purple-900 text-sm sm:text-base">Vacancy Details</div>
              <table className="w-full border-collapse text-sm">
                <tbody>
                  <tr className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                    <td className={`p-2 sm:p-3 font-medium w-[40%] sm:w-[35%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>Total Posts</td>
                    <td className={`p-2 sm:p-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{job.totalPosts}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Vacancy table (category wise) */}
          {job.vacancyDetails && job.vacancyDetails.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-teal-100 px-4 py-2 font-bold text-teal-900 text-sm sm:text-base">Category Wise Vacancy</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead><tr className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}><th className="p-2 text-left">Post</th><th className="p-2">Gen</th><th className="p-2">EWS</th><th className="p-2">OBC</th><th className="p-2">SC</th><th className="p-2">ST</th><th className="p-2">Total</th></tr></thead>
                  <tbody>
                    {job.vacancyDetails.map((r, i) => <tr key={i} className={i % 2 === 1 ? (darkMode ? 'bg-gray-700/50' : 'bg-gray-50') : ''}><td className="p-2">{r.postName}</td><td className="p-2 text-center">{r.general ?? '—'}</td><td className="p-2 text-center">{r.ews ?? '—'}</td><td className="p-2 text-center">{r.obc ?? '—'}</td><td className="p-2 text-center">{r.sc ?? '—'}</td><td className="p-2 text-center">{r.st ?? '—'}</td><td className="p-2 text-center font-bold">{r.total ?? '—'}</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Eligibility */}
          {job.eligibilityDetails && job.eligibilityDetails.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-indigo-100 px-4 py-2 font-bold text-indigo-900 text-sm sm:text-base">Eligibility</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead><tr className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}><th className="p-2 text-left">Post</th><th className="p-2">Total</th><th className="p-2 text-left">Criteria</th></tr></thead>
                  <tbody>
                    {job.eligibilityDetails.map((r, i) => <tr key={i} className={i % 2 === 1 ? (darkMode ? 'bg-gray-700/50' : 'bg-gray-50') : ''}><td className="p-2">{r.postName}</td><td className="p-2 text-center">{r.total}</td><td className="p-2">{r.criteria}</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* How to Apply */}
          {job.howToApply && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-sky-100 px-4 py-2 font-bold text-sky-900 text-sm sm:text-base">How to Fill / Apply</div>
              <div className={`p-4 text-sm whitespace-pre-line leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{job.howToApply}</div>
            </div>
          )}

          {/* Extra / Custom Fields - added on the spot when inserting job (exclude internal keys like detail_table/title_*) */}
          {job.extraFields && (() => {
            const entries = Object.entries(job.extraFields).filter(([k]) =>
              k !== 'detail_table' &&
              k !== 'wp_blocks' &&
              !/^title_/i.test(k)
            );
            if (entries.length === 0) return null;
            return (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-indigo-600 text-white px-4 py-2 font-bold text-sm sm:text-base">Additional Information</div>
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {entries.map(([k, v]) => (
                    <tr key={k} className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                      <td className={`p-2 sm:p-3 font-medium w-[40%] sm:w-[35%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>{String(k).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                      <td className={`p-2 sm:p-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{String(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            );
          })()}

          {/* Important Links - Table */}
          <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="bg-sarkari-red text-white px-4 py-2 font-bold text-sm sm:text-base">Some Useful Important Links</div>
            <table className="w-full border-collapse text-sm">
              <tbody>
                {linkItems.map((item, i) => (
                  <tr key={i} className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                    <td className={`p-2 sm:p-3 font-medium w-[40%] sm:w-[35%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>{item.label}</td>
                    <td className={`p-2 sm:p-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium flex items-center gap-1">
                        <ExternalLink className="w-4 h-4 shrink-0" /> Click Here
                      </a>
                    </td>
                  </tr>
                ))}
                {linkItems.length === 0 && (
                  <tr><td colSpan={2} className={`p-4 text-center ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>No links added yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Related Jobs - internal linking for SEO */}
          <RelatedJobsBlock currentJobId={job.id} department={job.department} darkMode={darkMode} />

          {/* Default keywords block - SarkariResult style */}
          <div className={`mt-4 py-3 px-4 rounded-lg text-sm border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <span className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Sarkari Result | Sarkariresult | Sarkari Naukri | Govt Jobs | Sarkariresult.cloud</span>
          </div>
          {/* SEO: Internal links to key pages */}
          <div className={`mt-4 py-3 px-4 rounded-lg text-sm ${darkMode ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
            <span className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Sarkari Result | Sarkariresult – Also see: </span>
            <RouterLink to="/sarkari-result" className="text-sarkari-red hover:underline font-medium">Sarkari Result</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/up-police-result" className="text-sarkari-red hover:underline font-medium">UP Police Result</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/sarkari-naukri" className="text-sarkari-red hover:underline font-medium">Sarkari Naukri</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/ssc-result" className="text-sarkari-red hover:underline font-medium">SSC Result</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/railway-result" className="text-sarkari-red hover:underline font-medium">Railway Result</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/bank-jobs" className="text-sarkari-red hover:underline font-medium">Bank Jobs</RouterLink>
          </div>

          {/* Apply & Share Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mt-6">
            <button 
              onClick={() => !expirationStatus?.isExpired && primaryApplyUrl && window.open(primaryApplyUrl, '_blank')}
              disabled={expirationStatus?.isExpired || !primaryApplyUrl}
              className={`py-3 px-6 rounded-lg font-bold flex items-center justify-center gap-2 ${
                expirationStatus?.isExpired ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              <ExternalLink className="w-5 h-5" />
              {expirationStatus?.isExpired ? 'Application Closed' : 'Apply Online'}
            </button>
            <button 
              className={`py-3 px-6 rounded-lg border font-medium flex items-center justify-center gap-2 ${darkMode ? 'border-gray-600 hover:bg-gray-700 text-white' : 'border-gray-300 hover:bg-gray-50 text-gray-900'}`}
              onClick={async () => {
                const shareData = {
                  title: job.title,
                  text: `Last Date: ${lastDate ? new Date(lastDate).toLocaleDateString('en-IN') : 'TBA'}\nApply: ${job.applyLink || job.officialWebsite || 'TBA'}`,
                  url: jobUrl,
                };
                if (navigator.share) {
                  try {
                    await navigator.share(shareData);
                    alert('Shared! ✅');
                  } catch (e) {
                    if (e.name !== 'AbortError') {
                      navigator.clipboard?.writeText(`${job.title}\n${shareData.text}\n${jobUrl}`);
                      alert('Link copied! 📋');
                    }
                  }
                } else {
                  navigator.clipboard?.writeText(`${job.title}\n${shareData.text}\n${jobUrl}`);
                  alert('Link copied! 📋');
                }
              }}
            >
              <Share2 className="w-4 h-4" /> Share Job
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ✅ ADMIT CARD DETAIL PAGE - Table layout (left=field, right=details) like SarkariResult PDF
  const AdmitCardDetailPage = () => {
    const { admitCardId } = useParams();
    const navigate = useNavigate();
    const [card, setCard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
      const load = async () => {
        if (!admitCardId) return;
        setLoading(true);
        setError(null);
        const res = await fetchAdmitCardById(parseInt(admitCardId));
        setLoading(false);
        if (res.success && res.data) setCard(res.data);
        else setError('Admit card not found');
      };
      load();
    }, [admitCardId]);

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null;
    const fmtFee = (v) => v ? (/^[\d]+$/.test(String(v)) ? `₹${v}` : v) : null;

    if (loading) {
      return (
        <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sarkari-red"></div>
        </div>
      );
    }
    if (error || !card) {
      return (
        <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          <div className="text-center">
            <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Admit Card Not Found</h2>
            {window.history.length > 2 && (
              <button onClick={() => navigate(-1)} className="bg-sarkari-red text-white px-4 py-2 rounded-lg hover:bg-red-700">Back</button>
            )}
          </div>
        </div>
      );
    }

    const importantDates = [
      { label: 'Application Begin', value: fmtDate(card.applicationBegin) },
      { label: 'Last Date for Apply Online', value: fmtDate(card.lastDateApply) },
      { label: 'Last Date Pay Exam Fee', value: fmtDate(card.lastDateFee) },
      { label: 'Correction Window', value: card.correctionDate },
      { label: 'Applications Status Available', value: card.applicationsStatusDate },
      { label: 'Exam Date', value: card.examDateDetail || card.examDate },
      { label: 'Exam City Available', value: card.examCityDate },
      { label: 'Admit Card Available', value: card.admitCardAvailable },
      { label: 'Admit Card Release Date', value: fmtDate(card.admitCardReleaseDate) },
    ].filter(r => r.value);

    const feeRows = [
      { label: 'General / OBC / EWS', value: fmtFee(card.feeGeneral) },
      { label: 'SC / ST', value: fmtFee(card.feeScst) },
      { label: 'PH (Divyang)', value: fmtFee(card.feePh) },
    ].filter(r => r.value);

    const ageRows = [
      { label: 'Minimum Age', value: card.ageMin },
      { label: 'Maximum Age', value: card.ageMax },
      { label: 'Age Relaxation', value: card.ageRelaxation },
    ].filter(r => r.value);

    const linkItems = [
      { label: 'Download Admit Card', url: card.admitCardLink },
      ...(card.importantLinks || []).map(l => ({ label: l.label || l.title || 'Link', url: l.url })),
      { label: 'Official Website', url: card.officialWebsite },
    ].filter(l => l.url);

    const admitUrl = `${SITE_URL}/admit-card/${card.id}`;
    const admitDesc = `${card.examName} - Admit Card. Sarkari Result | Sarkariresult. Sarkari Naukri. ${card.department}. Download at Sarkariresult.cloud.`.slice(0, 160);
    const admitBreadcrumb = { '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` }, { '@type': 'ListItem', position: 2, name: 'Admit Card', item: `${SITE_URL}/admit-card` }, { '@type': 'ListItem', position: 3, name: `${card.examName} Admit Card`, item: admitUrl }] };
    return (
      <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <SeoHead title={`${card.examName} - Admit Card | Sarkari Result | Sarkariresult.cloud`} description={admitDesc} schema={[admitBreadcrumb]} canonical={admitUrl} />
        <div className="max-w-4xl mx-auto px-3 py-4 sm:p-6">
          <div className="mb-4">
            {window.history.length > 2 && (
              <button onClick={() => navigate(-1)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${darkMode ? 'border-gray-600 hover:bg-gray-700 text-white' : 'border-gray-300 hover:bg-gray-100 text-gray-900'}`}>
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
          </div>

          <div className={`rounded-t-lg overflow-hidden border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="bg-sarkari-red text-white px-4 py-4 text-center">
              <div className="text-lg sm:text-xl font-bold uppercase tracking-wide">SARKARIRESULT.CLOUD</div>
              <div className="text-xs sm:text-sm font-medium mt-1 opacity-95">WWW.SARKARIRESULT.CLOUD</div>
            </div>
            <div className="p-4 sm:p-6">
              <h1 className={`text-xl sm:text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{card.examName}</h1>
              {card.postDateUpdate && <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Post Date / Update: {card.postDateUpdate}</p>}
              {card.shortInformation && <p className={`mt-3 text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{card.shortInformation}</p>}
              <p className={`mt-2 font-semibold text-sarkari-red ${darkMode ? 'text-red-400' : ''}`}>{card.department}</p>
            </div>
          </div>

          {/* Important Dates - Table */}
          {importantDates.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-amber-100 px-4 py-2 font-bold text-amber-900 text-sm sm:text-base">Important Dates</div>
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {importantDates.map((r, i) => (
                    <tr key={i} className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                      <td className={`p-2 sm:p-3 font-medium w-[40%] sm:w-[35%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>{r.label}</td>
                      <td className={`p-2 sm:p-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{r.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Application Fee - Table */}
          {feeRows.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-green-100 px-4 py-2 font-bold text-green-900 text-sm sm:text-base">Application Fee</div>
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {feeRows.map((r, i) => (
                    <tr key={i} className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                      <td className={`p-2 sm:p-3 font-medium w-[40%] sm:w-[35%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>{r.label}</td>
                      <td className={`p-2 sm:p-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{r.value}</td>
                    </tr>
                  ))}
                  {card.paymentMode && (
                    <tr className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                      <td className={`p-2 sm:p-3 font-medium w-[40%] sm:w-[35%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>Payment Mode</td>
                      <td className={`p-2 sm:p-3 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{card.paymentMode}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Age Limit - Table */}
          {ageRows.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-blue-100 px-4 py-2 font-bold text-blue-900 text-sm sm:text-base">Age Limit</div>
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {ageRows.map((r, i) => (
                    <tr key={i} className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                      <td className={`p-2 sm:p-3 font-medium w-[40%] sm:w-[35%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>{r.label}</td>
                      <td className={`p-2 sm:p-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{r.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Vacancy / Total Posts */}
          {card.totalPosts && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-purple-100 px-4 py-2 font-bold text-purple-900 text-sm sm:text-base">Vacancy Details</div>
              <table className="w-full border-collapse text-sm">
                <tbody>
                  <tr className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                    <td className={`p-2 sm:p-3 font-medium w-[40%] sm:w-[35%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>Total Posts</td>
                    <td className={`p-2 sm:p-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{card.totalPosts}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Vacancy table (category wise) */}
          {card.vacancyDetails && card.vacancyDetails.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-teal-100 px-4 py-2 font-bold text-teal-900 text-sm sm:text-base">Category Wise Vacancy</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead><tr className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}><th className="p-2 text-left">Post</th><th className="p-2">Gen</th><th className="p-2">EWS</th><th className="p-2">OBC</th><th className="p-2">SC</th><th className="p-2">ST</th><th className="p-2">Total</th></tr></thead>
                  <tbody>
                    {card.vacancyDetails.map((r, i) => <tr key={i} className={i % 2 === 1 ? (darkMode ? 'bg-gray-700/50' : 'bg-gray-50') : ''}><td className="p-2">{r.postName}</td><td className="p-2 text-center">{r.general ?? '—'}</td><td className="p-2 text-center">{r.ews ?? '—'}</td><td className="p-2 text-center">{r.obc ?? '—'}</td><td className="p-2 text-center">{r.sc ?? '—'}</td><td className="p-2 text-center">{r.st ?? '—'}</td><td className="p-2 text-center font-bold">{r.total ?? '—'}</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Eligibility */}
          {card.eligibilityDetails && card.eligibilityDetails.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-indigo-100 px-4 py-2 font-bold text-indigo-900 text-sm sm:text-base">Eligibility</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead><tr className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}><th className="p-2 text-left">Post</th><th className="p-2">Total</th><th className="p-2 text-left">Criteria</th></tr></thead>
                  <tbody>
                    {card.eligibilityDetails.map((r, i) => <tr key={i} className={i % 2 === 1 ? (darkMode ? 'bg-gray-700/50' : 'bg-gray-50') : ''}><td className="p-2">{r.postName}</td><td className="p-2 text-center">{r.total}</td><td className="p-2">{r.criteria}</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* How to Apply */}
          {card.howToApply && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-sky-100 px-4 py-2 font-bold text-sky-900 text-sm sm:text-base">How to Fill / Apply</div>
              <div className={`p-4 text-sm whitespace-pre-line leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{card.howToApply}</div>
            </div>
          )}

          {/* Important Links - Table (left=label, right=Click Here link) */}
          <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="bg-sarkari-red text-white px-4 py-2 font-bold text-sm sm:text-base">Some Useful Important Links</div>
            <table className="w-full border-collapse text-sm">
              <tbody>
                {linkItems.map((item, i) => (
                  <tr key={i} className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                    <td className={`p-2 sm:p-3 font-medium w-[40%] sm:w-[35%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>{item.label}</td>
                    <td className={`p-2 sm:p-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium flex items-center gap-1">
                        <ExternalLink className="w-4 h-4 shrink-0" /> Click Here
                      </a>
                    </td>
                  </tr>
                ))}
                {linkItems.length === 0 && (
                  <tr><td colSpan={2} className={`p-4 text-center ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>No links added yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={`mt-4 py-3 px-4 rounded-lg text-sm border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <span className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Sarkari Result | Sarkariresult | Sarkari Naukri | Govt Jobs | Sarkariresult.cloud</span>
          </div>
          <div className={`mt-4 py-3 px-4 rounded-lg text-sm ${darkMode ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
            <span className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Sarkari Result | Sarkariresult – Also see: </span>
            <RouterLink to="/sarkari-result" className="text-sarkari-red hover:underline font-medium">Sarkari Result</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/up-police-result" className="text-sarkari-red hover:underline font-medium">UP Police Result</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/sarkari-naukri" className="text-sarkari-red hover:underline font-medium">Sarkari Naukri</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/ssc-result" className="text-sarkari-red hover:underline font-medium">SSC Result</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/railway-result" className="text-sarkari-red hover:underline font-medium">Railway Result</RouterLink>
          </div>
        </div>
      </div>
    );
  };

  // ✅ ANSWER KEY DETAIL PAGE - Table layout (left=field, right=details) like SarkariResult
  const AnswerKeyDetailPage = () => {
    const { answerKeyId } = useParams();
    const navigate = useNavigate();
    const [key, setKey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
      const load = async () => {
        if (!answerKeyId) return;
        setLoading(true);
        setError(null);
        const res = await fetchAnswerKeyById(parseInt(answerKeyId));
        setLoading(false);
        if (res.success && res.data) setKey(res.data);
        else setError('Answer key not found');
      };
      load();
    }, [answerKeyId]);

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null;
    const fmtFee = (v) => v ? (/^[\d]+$/.test(String(v)) ? `₹${v}` : v) : null;

    if (loading) {
      return (
        <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sarkari-red"></div>
        </div>
      );
    }
    if (error || !key) {
      return (
        <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          <div className="text-center">
            <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Answer Key Not Found</h2>
            {window.history.length > 2 && (
              <button onClick={() => navigate(-1)} className="bg-sarkari-red text-white px-4 py-2 rounded-lg hover:bg-red-700">Back</button>
            )}
          </div>
        </div>
      );
    }

    const importantDates = [
      { label: 'Application Begin', value: fmtDate(key.applicationBegin) },
      { label: 'Last Date for Apply Online', value: fmtDate(key.lastDateApply) },
      { label: 'Last Date Pay Exam Fee', value: fmtDate(key.lastDateFee) },
      { label: 'Correction Window', value: key.correctionDate },
      { label: 'Applications Status Available', value: key.applicationsStatusDate },
      { label: 'Exam Date', value: key.examDateDetail || key.examDate },
      { label: 'Exam City Available', value: key.examCityDate },
      { label: 'Answer Key Available', value: key.answerKeyAvailable },
      { label: 'Answer Key Release Date', value: fmtDate(key.answerKeyReleaseDate) },
    ].filter(r => r.value);

    const feeRows = [
      { label: 'General / OBC / EWS', value: fmtFee(key.feeGeneral) },
      { label: 'SC / ST', value: fmtFee(key.feeScst) },
      { label: 'PH (Divyang)', value: fmtFee(key.feePh) },
    ].filter(r => r.value);

    const ageRows = [
      { label: 'Minimum Age', value: key.ageMin },
      { label: 'Maximum Age', value: key.ageMax },
      { label: 'Age Relaxation', value: key.ageRelaxation },
    ].filter(r => r.value);

    const linkItems = [
      { label: 'Download Answer Key', url: key.answerKeyLink },
      ...(key.importantLinks || []).map(l => ({ label: l.label || l.title || 'Link', url: l.url })),
      { label: 'Official Website', url: key.officialWebsite },
    ].filter(l => l.url);

    const keyUrl = `${SITE_URL}/answer-key/${key.id}`;
    const keyDesc = `${key.examName} - Answer Key. Sarkari Result | Sarkariresult. Sarkari Naukri. ${key.department}. Check at Sarkariresult.cloud.`.slice(0, 160);
    const keyBreadcrumb = { '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` }, { '@type': 'ListItem', position: 2, name: 'Answer Key', item: `${SITE_URL}/answer-key` }, { '@type': 'ListItem', position: 3, name: `${key.examName} Answer Key`, item: keyUrl }] };
    return (
      <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <SeoHead title={`${key.examName} - Answer Key | Sarkari Result | Sarkariresult.cloud`} description={keyDesc} schema={[keyBreadcrumb]} canonical={keyUrl} />
        <div className="max-w-4xl mx-auto px-3 py-4 sm:p-6">
          <div className="mb-4">
            {window.history.length > 2 && (
              <button onClick={() => navigate(-1)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${darkMode ? 'border-gray-600 hover:bg-gray-700 text-white' : 'border-gray-300 hover:bg-gray-100 text-gray-900'}`}>
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
          </div>

          <div className={`rounded-t-lg overflow-hidden border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="bg-sarkari-red text-white px-4 py-4 text-center">
              <div className="text-lg sm:text-xl font-bold uppercase tracking-wide">SARKARIRESULT.CLOUD</div>
              <div className="text-xs sm:text-sm font-medium mt-1 opacity-95">WWW.SARKARIRESULT.CLOUD</div>
            </div>
            <div className="p-4 sm:p-6">
              <h1 className={`text-xl sm:text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{key.examName}</h1>
              {key.postDateUpdate && <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Post Date / Update: {key.postDateUpdate}</p>}
              {key.shortInformation && <p className={`mt-3 text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{key.shortInformation}</p>}
              <p className={`mt-2 font-semibold text-sarkari-red ${darkMode ? 'text-red-400' : ''}`}>{key.department}</p>
            </div>
          </div>

          {importantDates.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-amber-100 px-4 py-2 font-bold text-amber-900 text-sm sm:text-base">Important Dates</div>
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {importantDates.map((r, i) => (
                    <tr key={i} className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                      <td className={`p-2 sm:p-3 font-medium w-[40%] sm:w-[35%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>{r.label}</td>
                      <td className={`p-2 sm:p-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{r.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {feeRows.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-green-100 px-4 py-2 font-bold text-green-900 text-sm sm:text-base">Application Fee</div>
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {feeRows.map((r, i) => (
                    <tr key={i} className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                      <td className={`p-2 sm:p-3 font-medium w-[40%] sm:w-[35%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>{r.label}</td>
                      <td className={`p-2 sm:p-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{r.value}</td>
                    </tr>
                  ))}
                  {key.paymentMode && (
                    <tr className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                      <td className={`p-2 sm:p-3 font-medium w-[40%] sm:w-[35%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>Payment Mode</td>
                      <td className={`p-2 sm:p-3 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{key.paymentMode}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {ageRows.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-blue-100 px-4 py-2 font-bold text-blue-900 text-sm sm:text-base">Age Limit</div>
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {ageRows.map((r, i) => (
                    <tr key={i} className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                      <td className={`p-2 sm:p-3 font-medium w-[40%] sm:w-[35%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>{r.label}</td>
                      <td className={`p-2 sm:p-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{r.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {key.totalPosts && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-purple-100 px-4 py-2 font-bold text-purple-900 text-sm sm:text-base">Vacancy Details</div>
              <table className="w-full border-collapse text-sm">
                <tbody>
                  <tr className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                    <td className={`p-2 sm:p-3 font-medium w-[40%] sm:w-[35%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>Total Posts</td>
                    <td className={`p-2 sm:p-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{key.totalPosts}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {key.vacancyDetails && key.vacancyDetails.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-teal-100 px-4 py-2 font-bold text-teal-900 text-sm sm:text-base">Category Wise Vacancy</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead><tr className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}><th className="p-2 text-left">Post</th><th className="p-2">Gen</th><th className="p-2">EWS</th><th className="p-2">OBC</th><th className="p-2">SC</th><th className="p-2">ST</th><th className="p-2">Total</th></tr></thead>
                  <tbody>
                    {key.vacancyDetails.map((r, i) => <tr key={i} className={i % 2 === 1 ? (darkMode ? 'bg-gray-700/50' : 'bg-gray-50') : ''}><td className="p-2">{r.postName}</td><td className="p-2 text-center">{r.general ?? '—'}</td><td className="p-2 text-center">{r.ews ?? '—'}</td><td className="p-2 text-center">{r.obc ?? '—'}</td><td className="p-2 text-center">{r.sc ?? '—'}</td><td className="p-2 text-center">{r.st ?? '—'}</td><td className="p-2 text-center font-bold">{r.total ?? '—'}</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {key.eligibilityDetails && key.eligibilityDetails.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-indigo-100 px-4 py-2 font-bold text-indigo-900 text-sm sm:text-base">Eligibility</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead><tr className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}><th className="p-2 text-left">Post</th><th className="p-2">Total</th><th className="p-2 text-left">Criteria</th></tr></thead>
                  <tbody>
                    {key.eligibilityDetails.map((r, i) => <tr key={i} className={i % 2 === 1 ? (darkMode ? 'bg-gray-700/50' : 'bg-gray-50') : ''}><td className="p-2">{r.postName}</td><td className="p-2 text-center">{r.total}</td><td className="p-2">{r.criteria}</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {key.howToApply && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-sky-100 px-4 py-2 font-bold text-sky-900 text-sm sm:text-base">How to Fill / Apply</div>
              <div className={`p-4 text-sm whitespace-pre-line leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{key.howToApply}</div>
            </div>
          )}

          <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="bg-sarkari-red text-white px-4 py-2 font-bold text-sm sm:text-base">Some Useful Important Links</div>
            <table className="w-full border-collapse text-sm">
              <tbody>
                {linkItems.map((item, i) => (
                  <tr key={i} className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                    <td className={`p-2 sm:p-3 font-medium w-[40%] sm:w-[35%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>{item.label}</td>
                    <td className={`p-2 sm:p-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium flex items-center gap-1">
                        <ExternalLink className="w-4 h-4 shrink-0" /> Click Here
                      </a>
                    </td>
                  </tr>
                ))}
                {linkItems.length === 0 && (
                  <tr><td colSpan={2} className={`p-4 text-center ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>No links added yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={`mt-4 py-3 px-4 rounded-lg text-sm border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <span className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Sarkari Result | Sarkariresult | Sarkari Naukri | Govt Jobs | Sarkariresult.cloud</span>
          </div>
          <div className={`mt-4 py-3 px-4 rounded-lg text-sm ${darkMode ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
            <span className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Sarkari Result | Sarkariresult – Also see: </span>
            <RouterLink to="/sarkari-result" className="text-sarkari-red hover:underline font-medium">Sarkari Result</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/up-police-result" className="text-sarkari-red hover:underline font-medium">UP Police Result</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/sarkari-naukri" className="text-sarkari-red hover:underline font-medium">Sarkari Naukri</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/ssc-result" className="text-sarkari-red hover:underline font-medium">SSC Result</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/railway-result" className="text-sarkari-red hover:underline font-medium">Railway Result</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/bank-jobs" className="text-sarkari-red hover:underline font-medium">Bank Jobs</RouterLink>
          </div>
        </div>
      </div>
    );
  };

  // ✅ ADMISSION DETAIL PAGE
  const AdmissionDetailPage = () => {
    const { admissionId } = useParams();
    const navigate = useNavigate();
    const [ad, setAd] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
      const load = async () => {
        if (!admissionId) return;
        setLoading(true);
        setError(null);
        const res = await fetchAdmissionById(parseInt(admissionId));
        setLoading(false);
        if (res.success && res.data) setAd(res.data);
        else setError('Admission not found');
      };
      load();
    }, [admissionId]);

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null;

    if (loading) {
      return (
        <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sarkari-red"></div>
        </div>
      );
    }
    if (error || !ad) {
      return (
        <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          <div className="text-center">
            <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Admission Not Found</h2>
            {window.history.length > 2 && (
              <button onClick={() => navigate(-1)} className="bg-sarkari-red text-white px-4 py-2 rounded-lg hover:bg-red-700">Back</button>
            )}
          </div>
        </div>
      );
    }

    const importantDates = [
      { label: 'Exam Date', value: fmtDate(ad.exam_date) },
      { label: 'Last Date', value: fmtDate(ad.last_date) },
    ].filter(r => r.value);

    const linkItems = [
      { label: 'Apply Online / Admission Link', url: ad.admission_link },
      { label: 'Official Website', url: ad.official_website },
    ].filter(l => l.url);

    const adUrl = `${SITE_URL}/admission/${ad.id}`;
    const adDesc = `${ad.exam_name} - Admissions. Sarkari Result | Sarkari Naukri. ${ad.department}. Sarkariresult.cloud.`.slice(0, 160);
    return (
      <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <SeoHead title={`${ad.exam_name} - Admissions | Sarkari Result | Sarkariresult.cloud`} description={adDesc} canonical={adUrl} />
        <div className="max-w-4xl mx-auto px-3 py-4 sm:p-6">
          <div className="mb-4">
            {window.history.length > 2 && (
              <button onClick={() => navigate(-1)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${darkMode ? 'border-gray-600 hover:bg-gray-700 text-white' : 'border-gray-300 hover:bg-gray-100 text-gray-900'}`}>
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
          </div>

          <div className={`rounded-t-lg overflow-hidden border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="bg-teal-600 text-white px-4 py-4 text-center">
              <div className="text-lg sm:text-xl font-bold uppercase tracking-wide">SARKARIRESULT.CLOUD</div>
              <div className="text-xs sm:text-sm font-medium mt-1 opacity-95">ADMISSIONS | WWW.SARKARIRESULT.CLOUD</div>
            </div>
            <div className="p-4 sm:p-6">
              <h1 className={`text-xl sm:text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{ad.exam_name}</h1>
              {ad.shortInformation && <p className={`mt-3 text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{ad.shortInformation}</p>}
              <p className={`mt-2 font-semibold text-teal-600 ${darkMode ? 'text-teal-400' : ''}`}>{ad.department}</p>
            </div>
          </div>

          {importantDates.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-teal-100 px-4 py-2 font-bold text-teal-900 text-sm sm:text-base">Important Dates</div>
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {importantDates.map((r, i) => (
                    <tr key={i} className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                      <td className={`p-2 sm:p-3 font-medium w-[40%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>{r.label}</td>
                      <td className={`p-2 sm:p-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{r.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {linkItems.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-sarkari-red text-white px-4 py-2 font-bold text-sm sm:text-base">Important Links</div>
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {linkItems.map((item, i) => (
                    <tr key={i} className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                      <td className={`p-2 sm:p-3 font-medium w-[40%] ${darkMode ? 'bg-gray-700/50 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>{item.label}</td>
                      <td className={`p-2 sm:p-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium flex items-center gap-1">
                          <ExternalLink className="w-4 h-4 shrink-0" /> Click Here
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className={`mt-4 py-3 px-4 rounded-lg text-sm ${darkMode ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
            <RouterLink to="/admissions" className="text-sarkari-red hover:underline font-medium">← Back to Admissions</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/latest-jobs" className="text-sarkari-red hover:underline font-medium">Latest Jobs</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/results" className="text-sarkari-red hover:underline font-medium">Sarkari Result</RouterLink>
          </div>
        </div>
      </div>
    );
  };

  // ✅ RESULT DETAIL PAGE - SarkariResult.com style
  const ResultDetailPage = () => {
    const { resultId } = useParams();
    const navigate = useNavigate();
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
      const load = async () => {
        if (!resultId) return;
        setLoading(true);
        setError(null);
        const res = await fetchResultById(parseInt(resultId));
        setLoading(false);
        if (res.success && res.data) setResult(res.data);
        else setError('Result not found');
      };
      load();
    }, [resultId]);

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

    if (loading) {
      return (
        <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sarkari-red"></div>
        </div>
      );
    }
    if (error || !result) {
      return (
        <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          <div className="text-center">
            <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Result Not Found</h2>
            <button onClick={() => navigate('/')} className="bg-sarkari-red text-white px-4 py-2 rounded-lg hover:bg-red-700">Back to Home</button>
          </div>
        </div>
      );
    }

    const resultUrl = `${SITE_URL}/result/${result.id}`;
    const resultDesc = `${result.examName} - Sarkari Result | Sarkariresult. ${result.department}. Check Sarkari Exam Result at Sarkariresult.cloud.`.slice(0, 160);
    const resultBreadcrumb = { '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` }, { '@type': 'ListItem', position: 2, name: 'Results', item: `${SITE_URL}/results` }, { '@type': 'ListItem', position: 3, name: `${result.examName} Sarkari Result`, item: resultUrl }] };
    return (
      <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <SeoHead title={`${result.examName} - Sarkari Result | Sarkariresult.cloud`} description={resultDesc} schema={[resultBreadcrumb]} canonical={resultUrl} />
        <div className="max-w-4xl mx-auto px-3 py-4 sm:p-6">
          {/* Back */}
          <div className="mb-4">
            {window.history.length > 2 && (
              <button onClick={() => navigate(-1)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${darkMode ? 'border-gray-600 hover:bg-gray-700 text-white' : 'border-gray-300 hover:bg-gray-100 text-gray-900'}`}>
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
          </div>

          {/* Header - SarkariResult style */}
          <div className={`rounded-t-lg overflow-hidden border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="bg-sarkari-red text-white px-4 py-4 text-center">
              <div className="text-lg sm:text-xl font-bold uppercase tracking-wide">SARKARIRESULT.CLOUD</div>
              <div className="text-xs sm:text-sm font-medium mt-1 opacity-95">WWW.SARKARIRESULT.CLOUD</div>
            </div>
            <div className="p-4 sm:p-6">
              <h1 className={`text-xl sm:text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{result.examName}</h1>
              {result.postDateUpdate && <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Post Date / Update: {result.postDateUpdate}</p>}
              {result.shortInformation && <p className={`mt-3 text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{result.shortInformation}</p>}
              <p className={`mt-2 font-semibold text-sarkari-red ${darkMode ? 'text-red-400' : ''}`}>{result.department}</p>
            </div>
          </div>

          {/* Dynamic tables (from detail_table): merge, bold, align, text/cell colour */}
          {result.detailTable && (() => {
            const toCell = (v) => { const o = typeof v === 'object' && v != null && 'text' in v ? v : {}; return { text: String(o.text ?? v ?? ''), bold: !!o.bold, align: o.align || 'left', textColor: o.textColor || '', fill: o.fill || '', colspan: Math.max(1, parseInt(o.colspan, 10) || 1) }; };
            const toRow = (r, colCount) => {
              if (Array.isArray(r)) { const cells = r.slice(0, colCount).map(toCell); while (cells.length < colCount) cells.push(toCell('')); return { merged: false, cells }; }
              const merged = !!r.merged;
              let cells = Array.isArray(r.cells) ? r.cells.map(toCell) : [toCell('')];
              if (merged) cells = [cells[0] || toCell('')];
              else while (cells.length < colCount) cells.push(toCell(''));
              return { merged, cells };
            };
            const tables = Array.isArray(result.detailTable.tables) ? result.detailTable.tables : null;
            const single = !tables && Array.isArray(result.detailTable.columns) && result.detailTable.columns.length > 0 ? [{ columns: result.detailTable.columns, rows: result.detailTable.rows || [] }] : null;
            const toRender = tables || single;
            if (!toRender || toRender.length === 0) return null;
            return toRender.map((tbl, ti) => {
              const cols = tbl.columns || [];
              const colCount = cols.length || 1;
              const rows = (Array.isArray(tbl.rows) ? tbl.rows : []).map((r) => toRow(r, colCount));
              return (
                <div key={ti} className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className={darkMode ? 'bg-gray-700' : 'bg-green-100'}>
                          {cols.map((col, i) => (
                            <th key={i} className={`p-2 sm:p-3 text-left font-bold border-r last:border-r-0 ${darkMode ? 'text-gray-200 border-gray-600' : 'text-green-900 border-gray-300'}`}>{col || `Column ${i + 1}`}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, ri) => (
                          <tr key={ri} className={darkMode ? 'border-b border-gray-600' : 'border-b border-gray-200'}>
                            {row.merged && row.cells?.length === 1 ? (
                              <td colSpan={colCount} className={`p-2 sm:p-3 whitespace-pre-line border-r last:border-r-0 ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-300'}`} style={{ textAlign: row.cells[0].align || 'left', color: row.cells[0].textColor || undefined, backgroundColor: row.cells[0].fill || undefined }}>
                                {row.cells[0].bold ? <strong>{row.cells[0].text || '—'}</strong> : (row.cells[0].text || '—')}
                              </td>
                            ) : (
                              (row.cells || []).map((cell, ci) => (
                                <td key={ci} colSpan={cell.colspan || 1} className={`p-2 sm:p-3 whitespace-pre-line border-r last:border-r-0 ${darkMode ? 'text-gray-300 border-gray-600' : 'text-gray-700 border-gray-300'}`} style={{ textAlign: cell.align || 'left', color: cell.textColor || undefined, backgroundColor: cell.fill || undefined }}>
                                  {cell.bold ? <strong>{cell.text || '—'}</strong> : (cell.text || '—')}
                                </td>
                              ))
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            });
          })()}

          {(!result.detailTable?.tables?.length && !(result.detailTable?.columns?.length)) && (
            <>
          {/* Important Dates */}
          {(result.applicationBegin || result.lastDateApply || result.examDate || result.resultAvailableDate || result.admitCardDate) && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-amber-100 px-4 py-2 font-bold text-amber-900 text-sm sm:text-base">Important Dates</div>
              <div className={`p-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {result.applicationBegin && <div><strong>Application Begin:</strong> {fmtDate(result.applicationBegin)}</div>}
                {result.lastDateApply && <div><strong>Last Date Apply Online:</strong> {fmtDate(result.lastDateApply)}</div>}
                {result.lastDateFee && <div><strong>Last Date Pay Exam Fee:</strong> {fmtDate(result.lastDateFee)}</div>}
                {result.correctionDate && <div><strong>Correction Last Date:</strong> {result.correctionDate}</div>}
                {result.examDate && <div><strong>Exam Date:</strong> {result.examDate}</div>}
                {result.admitCardDate && <div><strong>Admit Card Available:</strong> {result.admitCardDate}</div>}
                {result.resultAvailableDate && <div><strong>Result Available:</strong> {result.resultAvailableDate}</div>}
              </div>
            </div>
          )}

          {/* Application Fee */}
          {(result.feeGeneral || result.feeScst || result.feeFemale) && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-green-100 px-4 py-2 font-bold text-green-900 text-sm sm:text-base">Application Fee</div>
              <div className={`p-4 space-y-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {result.feeGeneral && <div><strong>General / OBC / EWS:</strong> {/^[\d]+$/.test(String(result.feeGeneral)) ? `₹${result.feeGeneral}` : result.feeGeneral}</div>}
                {result.feeScst && <div><strong>SC / ST:</strong> {/^[\d]+$/.test(String(result.feeScst)) ? `₹${result.feeScst}` : result.feeScst}</div>}
                {result.feeFemale && <div><strong>All Category Female:</strong> {/^[\d]+$/.test(String(result.feeFemale)) ? `₹${result.feeFemale}` : result.feeFemale}</div>}
                {result.paymentMode && <p className="mt-2 text-xs opacity-80">{result.paymentMode}</p>}
              </div>
            </div>
          )}

          {/* Age Limit */}
          {(result.ageLimit || result.ageRelaxation) && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-blue-100 px-4 py-2 font-bold text-blue-900 text-sm sm:text-base">Age Limit</div>
              <div className={`p-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {result.ageLimit && <p>{result.ageLimit}</p>}
                {result.ageRelaxation && <p className="mt-1">{result.ageRelaxation}</p>}
              </div>
            </div>
          )}

          {/* Vacancy / Eligibility */}
          {result.totalPosts && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-purple-100 px-4 py-2 font-bold text-purple-900 text-sm sm:text-base">Vacancy Details</div>
              <div className={`p-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}><strong>Total:</strong> {result.totalPosts} Post(s)</div>
            </div>
          )}

          {result.eligibilityDetails && result.eligibilityDetails.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-indigo-100 px-4 py-2 font-bold text-indigo-900 text-sm sm:text-base">Eligibility</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead><tr className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}><th className="p-2 text-left">Post</th><th className="p-2">Total</th><th className="p-2 text-left">Criteria</th></tr></thead>
                  <tbody>
                    {result.eligibilityDetails.map((r, i) => <tr key={i} className={i % 2 === 1 ? (darkMode ? 'bg-gray-700/50' : 'bg-gray-50') : ''}><td className="p-2">{r.postName}</td><td className="p-2 text-center">{r.total}</td><td className="p-2">{r.criteria}</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result.vacancyDetails && result.vacancyDetails.length > 0 && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-teal-100 px-4 py-2 font-bold text-teal-900 text-sm sm:text-base">Category Wise Vacancy</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead><tr className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}><th className="p-2 text-left">Post</th><th className="p-2">Gen</th><th className="p-2">EWS</th><th className="p-2">OBC</th><th className="p-2">SC</th><th className="p-2">ST</th><th className="p-2">Total</th></tr></thead>
                  <tbody>
                    {result.vacancyDetails.map((r, i) => <tr key={i} className={i % 2 === 1 ? (darkMode ? 'bg-gray-700/50' : 'bg-gray-50') : ''}><td className="p-2">{r.postName}</td><td className="p-2 text-center">{r.general ?? '—'}</td><td className="p-2 text-center">{r.ews ?? '—'}</td><td className="p-2 text-center">{r.obc ?? '—'}</td><td className="p-2 text-center">{r.sc ?? '—'}</td><td className="p-2 text-center">{r.st ?? '—'}</td><td className="p-2 text-center font-bold">{r.total ?? '—'}</td></tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          )}

            </>
          )}
          {/* How to Apply */}
          {result.howToApply && (
            <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="bg-sky-100 px-4 py-2 font-bold text-sky-900 text-sm sm:text-base">How to Fill / Apply</div>
              <div className={`p-4 text-sm whitespace-pre-line leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{result.howToApply}</div>
            </div>
          )}

          {/* Important Links */}
          <div className={`mt-4 rounded-lg border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="bg-sarkari-red text-white px-4 py-2 font-bold text-sm sm:text-base">Some Useful Important Links</div>
            <div className="p-4 space-y-2">
              {result.resultLink && (
                <a href={result.resultLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 hover:underline font-medium">
                  <Download className="w-4 h-4" /> Download Result - Click Here
                </a>
              )}
              {(result.importantLinks || []).map((link, i) => (
                <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 hover:underline">
                  <ExternalLink className="w-4 h-4" /> {link.label || link.title || 'Link'}
                </a>
              ))}
              {result.officialWebsite && (
                <a href={result.officialWebsite} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 hover:underline">
                  <Globe className="w-4 h-4" /> Official Website
                </a>
              )}
              {!result.resultLink && result.importantLinks?.length === 0 && !result.officialWebsite && (
                <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>No links added yet.</p>
              )}
            </div>
          </div>

          {/* Default keywords block */}
          <div className={`mt-4 py-3 px-4 rounded-lg text-sm border ${darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <span className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Sarkari Result | Sarkariresult | Sarkari Naukri | Govt Jobs | Sarkariresult.cloud</span>
          </div>
          <div className={`mt-4 py-3 px-4 rounded-lg text-sm ${darkMode ? 'bg-gray-700/50' : 'bg-gray-100'}`}>
            <span className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Sarkari Result | Sarkariresult – Also see: </span>
            <RouterLink to="/sarkari-result" className="text-sarkari-red hover:underline font-medium">Sarkari Result</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/up-police-result" className="text-sarkari-red hover:underline font-medium">UP Police Result</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/sarkari-naukri" className="text-sarkari-red hover:underline font-medium">Sarkari Naukri</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/ssc-result" className="text-sarkari-red hover:underline font-medium">SSC Result</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/railway-result" className="text-sarkari-red hover:underline font-medium">Railway Result</RouterLink>
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}> | </span>
            <RouterLink to="/bank-jobs" className="text-sarkari-red hover:underline font-medium">Bank Jobs</RouterLink>
          </div>
        </div>
      </div>
    );
  };

  // ✅ ENHANCED JOB CARD - ALL HOOKS AT TOP LEVEL
  const LatestJobCard = React.memo(({ job }) => {
    const navigate = useNavigate();
    const daysRemaining = calculateDaysRemaining(job.lastDate);
    const expirationStatus = getExpirationStatus(daysRemaining);

    const handleJobCardClick = useCallback((e) => {
      if (e.target.tagName === 'BUTTON' || e.target.tagName === 'A' || e.target.closest('button') || e.target.closest('a')) {
        return;
      }
      navigate(`/job/${job.id}`);
    }, [navigate, job.id]);
    
    return (
      <div 
        className={`p-4 sm:p-6 rounded-lg sm:rounded-xl border hover:shadow-lg transition-all duration-300 cursor-pointer relative ${
          darkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-750' : 'bg-white border-gray-200 hover:bg-gray-50'
        } ${job.urgency === 'high' ? 'ring-2 ring-red-500' : job.urgency === 'medium' ? 'ring-1 ring-yellow-500' : ''}`}
        onClick={handleJobCardClick}
        onMouseEnter={() => prefetchJob(job.id)}
      >
        <RecentBadge date={job.post_date_update || job.notificationDate} />
        {/* Job Header */}
        <div className={`mb-3 sm:mb-4 p-3 sm:p-4 rounded-lg flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 ${
          expirationStatus?.isExpired ? 'bg-red-100 dark:bg-red-900/20' :
          daysRemaining <= 7 ? 'bg-blue-100 dark:bg-blue-900/20' :
          'bg-blue-100 dark:bg-blue-900/20'
        }`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <h3 className={`font-bold text-base sm:text-xl break-words pr-8 ${
                expirationStatus?.isExpired ? 'text-red-800 dark:text-red-300' :
                daysRemaining <= 7 ? 'text-blue-800 dark:text-blue-300' :
                'text-blue-800 dark:text-blue-300'
              }`}>
                {job.title}
              </h3>
            </div>
            
            {job.isNew && (
              <div className="mb-2">
                <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-semibold">NEW</span>
              </div>
            )}

            <p className={`text-sm mb-1 ${
              expirationStatus?.isExpired ? 'text-red-700 dark:text-red-400' :
              daysRemaining <= 7 ? 'text-blue-700 dark:text-blue-400' :
              'text-blue-700 dark:text-blue-400'
            }`}>
              <span className="font-semibold">Department:</span> {job.department}
            </p>
          </div>

          <div className="text-center sm:text-right flex-shrink-0">
            {expirationStatus?.isExpired ? (
              <div className="text-center">
                <span className="text-3xl sm:text-4xl">❌</span>
                <div className="text-red-800 font-bold text-sm mt-1">EXPIRED</div>
              </div>
            ) : (
              <div className="text-center">
                <span className="text-3xl sm:text-4xl">
                  {daysRemaining <= 1 ? '🚨' : daysRemaining <= 7 ? '⚠️' : '✅'}
                </span>
                <div className={`font-bold text-2xl sm:text-3xl mt-1 ${
                  daysRemaining <= 1 ? 'text-red-800 dark:text-red-300' :
                  daysRemaining <= 7 ? 'text-orange-800 dark:text-orange-300' :
                  'text-blue-800 dark:text-blue-300'
                }`}>
                  {daysRemaining}
                </div>
                <div className={`text-xs ${
                  daysRemaining <= 1 ? 'text-red-700 dark:text-red-400' :
                  daysRemaining <= 7 ? 'text-orange-700 dark:text-orange-400' :
                  'text-blue-700 dark:text-blue-400'
                }`}>
                  Days Left
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Job Details */}
        <div className={`p-3 sm:p-4 rounded-lg mb-3 sm:mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Job Details:
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            {job.location && (
              <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                <span className="font-semibold text-blue-600">📍 Location:</span> {job.location}
              </div>
            )}
            {job.totalPosts && (
              <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                <span className="font-semibold text-blue-600">👥 Total Posts:</span> {job.totalPosts}
              </div>
            )}
            {job.salary && job.salary !== 'As per norms' && (
              <div className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                <span className="font-semibold text-blue-600">💰 Salary:</span> 
                <span className={`ml-1 font-semibold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                  {job.salary}
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              !expirationStatus?.isExpired && handleApplyNow(job);
            }}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 text-base ${
              expirationStatus?.isExpired 
                ? 'bg-gray-400 text-white cursor-not-allowed opacity-75' 
                : job.applicationLink && job.applicationLink.trim() 
                ? daysRemaining <= 1 
                  ? 'bg-red-600 hover:bg-red-700 text-white hover:shadow-lg transform hover:scale-105 animate-pulse'
                  : daysRemaining <= 7 
                  ? 'bg-orange-600 hover:bg-orange-700 text-white hover:shadow-lg transform hover:scale-105'
                  : 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg transform hover:scale-105'
                : 'bg-gray-400 text-white cursor-not-allowed'
            }`}
            disabled={expirationStatus?.isExpired || !job.applicationLink || !job.applicationLink.trim()}
          >
            {expirationStatus?.isExpired ? (
              <>
                <XCircle className="w-5 h-5" />
                Application Closed
              </>
            ) : (
              <>
                <ExternalLink className="w-5 h-5" />
                {job.applicationLink && job.applicationLink.trim() ? 
                  daysRemaining <= 1 ? 'Apply Now - Last Day! 🚨' :
                  daysRemaining <= 7 ? 'Apply Now - Urgent! ⚠️' :
                  'Apply Now 🚀' : 'Link Not Available'
                }
              </>
            )}
          </button>

          <button 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/job/${job.id}`);
            }}
            className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 text-base ${
              darkMode 
                ? 'bg-purple-600 hover:bg-purple-700 text-white hover:shadow-lg transform hover:scale-105' 
                : 'bg-purple-600 hover:bg-purple-700 text-white hover:shadow-lg transform hover:scale-105'
            }`}
          >
            <Eye className="w-5 h-5" />
            View Details
          </button>
          
          <button 
            onClick={(e) => {
              e.stopPropagation();
              const shareText = `${job.title} at ${job.department}\n${daysRemaining > 0 ? `Only ${daysRemaining} days left to apply!` : 'Application deadline passed.'}\n${job.applicationLink || window.location.href}`;
              navigator.clipboard.writeText(shareText);
              alert('Job details copied to clipboard! 📋');
            }}
            className={`px-4 py-3 rounded-lg border transition-colors ${
              darkMode ? 'border-gray-600 hover:bg-gray-700 text-white' : 'border-gray-300 hover:bg-gray-50 text-gray-900'
            }`}
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  });

  // ✅ Section cards (Result, Admit Card, Answer Key, Admissions) - same style as Latest Jobs
  const ResultSectionCard = React.memo(({ item }) => {
    const navigate = useNavigate();
    return (
    <div
      className={`p-4 sm:p-6 rounded-lg sm:rounded-xl border hover:shadow-lg transition-all duration-300 cursor-pointer relative ${darkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-750' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
      onClick={(e) => { if (!e.target.closest('a')) navigate(`/result/${item.id}`); }}
      onMouseEnter={() => prefetchResult(item.id)}
    >
      <RecentBadge date={item.post_date_update || item.resultDate} />
      <div className="mb-3 p-3 sm:p-4 rounded-lg flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-blue-100 dark:bg-blue-900/20">
        <div className="flex-1 min-w-0 pr-8">
          <h3 className="font-bold text-base sm:text-xl break-words text-blue-800 dark:text-blue-300">{item.tileDisplayTitle || item.examName}</h3>
          <p className="text-sm text-blue-700 dark:text-blue-400 mt-1"><span className="font-semibold">Department:</span> {item.department}</p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        {item.resultDate && <p className="text-sm text-gray-500">Result Date: {new Date(item.resultDate).toLocaleDateString('en-IN')}</p>}
        <RouterLink to={`/result/${item.id}`} className="text-sarkari-red hover:underline text-sm font-medium">View Details →</RouterLink>
      </div>
    </div>
    );
  });

  const AdmitCardSectionCard = React.memo(({ item }) => {
    const navigate = useNavigate();
    return (
    <div
      className={`p-4 sm:p-6 rounded-lg sm:rounded-xl border hover:shadow-lg transition-all duration-300 cursor-pointer relative ${darkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-750' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
      onClick={(e) => { if (!e.target.closest('a')) navigate(`/admit-card/${item.id}`); }}
      onMouseEnter={() => prefetchAdmitCard(item.id)}
    >
      <RecentBadge date={item.post_date_update || item.admitCardReleaseDate} />
      <div className="mb-3 p-3 sm:p-4 rounded-lg flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-amber-50 dark:bg-amber-900/20">
        <div className="flex-1 min-w-0 pr-8">
          <h3 className="font-bold text-base sm:text-xl break-words text-amber-800 dark:text-amber-300">{item.tileDisplayTitle || item.examName}</h3>
          <p className="text-sm text-amber-700 dark:text-amber-400 mt-1"><span className="font-semibold">Department:</span> {item.department}</p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        {item.examDate && <p className="text-sm text-gray-500">Exam Date: {new Date(item.examDate).toLocaleDateString('en-IN')}</p>}
        <RouterLink to={`/admit-card/${item.id}`} className="text-sarkari-red hover:underline text-sm font-medium">View Details →</RouterLink>
      </div>
    </div>
    );
  });

  const AnswerKeySectionCard = React.memo(({ item }) => {
    const navigate = useNavigate();
    return (
    <div
      className={`p-4 sm:p-6 rounded-lg sm:rounded-xl border hover:shadow-lg transition-all duration-300 cursor-pointer relative ${darkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-750' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
      onClick={(e) => { if (!e.target.closest('a')) navigate(`/answer-key/${item.id}`); }}
      onMouseEnter={() => prefetchAnswerKey(item.id)}
    >
      <RecentBadge date={item.post_date_update || item.answerKeyReleaseDate} />
      <div className="mb-3 p-3 sm:p-4 rounded-lg flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-violet-50 dark:bg-violet-900/20">
        <div className="flex-1 min-w-0 pr-8">
          <h3 className="font-bold text-base sm:text-xl break-words text-violet-800 dark:text-violet-300">{item.tileDisplayTitle || item.examName}</h3>
          <p className="text-sm text-violet-700 dark:text-violet-400 mt-1"><span className="font-semibold">Department:</span> {item.department}</p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        {item.examDate && <p className="text-sm text-gray-500">Exam Date: {new Date(item.examDate).toLocaleDateString('en-IN')}</p>}
        <RouterLink to={`/answer-key/${item.id}`} className="text-sarkari-red hover:underline text-sm font-medium">View Details →</RouterLink>
      </div>
    </div>
    );
  });

  const AdmissionsSectionCard = React.memo(({ item }) => {
    const navigate = useNavigate();
    return (
    <div
      className={`p-4 sm:p-6 rounded-lg sm:rounded-xl border hover:shadow-lg transition-all duration-300 cursor-pointer ${darkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-750' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
      onClick={(e) => { if (!e.target.closest('a')) navigate(`/admission/${item.id}`); }}
      onMouseEnter={() => prefetchAdmission(item.id)}
    >
      <div className="mb-3 p-3 sm:p-4 rounded-lg flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-teal-50 dark:bg-teal-900/20">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-base sm:text-xl break-words text-teal-800 dark:text-teal-300">{item.tileDisplayTitle || item.examName}</h3>
          <p className="text-sm text-teal-700 dark:text-teal-400 mt-1"><span className="font-semibold">Department:</span> {item.department}</p>
        </div>
        <span className="bg-teal-600 text-white text-xs px-2 py-0.5 rounded font-semibold shrink-0 whitespace-nowrap">{item.lastDate ? `Last: ${new Date(item.lastDate).toLocaleDateString('en-IN')}` : (item.examDate ? new Date(item.examDate).toLocaleDateString('en-IN') : 'Admission')}</span>
      </div>
      <div className="flex items-center justify-between">
        {item.examDate && <p className="text-sm text-gray-500">Exam Date: {new Date(item.examDate).toLocaleDateString('en-IN')}</p>}
        <RouterLink to={`/admission/${item.id}`} className="text-sarkari-red hover:underline text-sm font-medium">View Details →</RouterLink>
      </div>
    </div>
    );
  });

  // ✅ MAIN APP COMPONENT - SARKARI JOB STYLE UI
  // Main nav - SEO-friendly URLs for indexing
  const mainNavItems = [
    { id: 'home', label: 'Home', path: '/' },
    { id: 'latest-jobs', label: 'Latest Jobs', path: '/latest-jobs' },
    { id: 'results', label: 'Results', path: '/results' },
    { id: 'admit-cards', label: 'Admit Card', path: '/admit-card' },
    { id: 'answer-keys', label: 'Answer Key', path: '/answer-key' },
    { id: 'admissions', label: 'Admissions', path: '/admissions' },
    { id: 'contact', label: 'Contact', path: '/contact' }
  ];

  // Sync currentPage with URL for SEO (indexable category pages)
  React.useEffect(() => {
    const p = location.pathname;
    if (p === '/latest-jobs') setCurrentPage('latest-jobs');
    else if (p === '/results') setCurrentPage('results');
    else if (p === '/admit-card') setCurrentPage('admit-cards');
    else if (p === '/answer-key') setCurrentPage('answer-keys');
    else if (p === '/admissions') setCurrentPage('admissions');
    else if (p === '/contact') setCurrentPage('contact');
    else if (p === '/') setCurrentPage('home');
  }, [location.pathname]);
  // Hamburger menu only - Sarkari Result, UP Police etc
  const hamburgerOnlyItems = [
    { id: 'sarkari-result', label: 'Sarkari Result', path: '/sarkari-result' },
    { id: 'sarkari-naukri', label: 'Sarkari Naukri', path: '/sarkari-naukri' },
    { id: 'up-police-result', label: 'UP Police Result', path: '/up-police-result' },
    { path: '/ssc-result', label: 'SSC Result' },
    { path: '/railway-result', label: 'Railway Result' },
    { path: '/upsc-result', label: 'UPSC Result' },
    { path: '/bank-jobs', label: 'Bank Jobs' },
  ];

  // Latest 8 jobs - admin-selected (showInTiles) ya fallback: last 7 days
  const latest8Jobs = React.useMemo(() => {
    const getDate = (d) => (d ? new Date(d).getTime() : 0);
    const withTs = jobPosts
      .filter((j) => j.title || j.department)
      .map((j) => ({ ...j, ts: getDate(j.notificationDate || j.postDate) }));
    const selected = withTs.filter((j) => j.showInTiles).sort((a, b) => (a.tileOrder || 999) - (b.tileOrder || 999));
    if (selected.length > 0) return selected.slice(0, 8);
    const now = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    return withTs
      .filter((j) => !j.ts || ((now - j.ts) <= sevenDaysMs && (now - j.ts) >= 0))
      .sort((a, b) => b.ts - a.ts)
      .slice(0, 8);
  }, [jobPosts]);

  // Running ticker - admin-selected (showInTicker) from ALL sections: Jobs, Results, Admit Cards, Answer Keys, Admissions
  const runningUpdates = React.useMemo(() => {
    const getDate = (d) => (d ? new Date(d).getTime() : 0);
    const items = [];
    jobPosts.filter((j) => j.showInTicker && (j.title || j.department)).forEach((j) => {
      items.push({ type: 'job', id: j.id, label: j.tileDisplayTitle || j.title || j.department, path: `/job/${j.id}`, order: j.tickerOrder ?? 999 });
    });
    results.filter((r) => r.showInTicker && (r.examName || r.department)).forEach((r) => {
      items.push({ type: 'result', id: r.id, label: r.tileDisplayTitle || r.examName || r.department, path: `/result/${r.id}`, order: r.tickerOrder ?? 999 });
    });
    admitCards.filter((a) => a.showInTicker && (a.examName || a.department)).forEach((a) => {
      items.push({ type: 'admit-card', id: a.id, label: a.tileDisplayTitle || a.examName || a.department, path: `/admit-card/${a.id}`, order: a.tickerOrder ?? 999 });
    });
    answerKeys.filter((k) => k.showInTicker && (k.examName || k.department)).forEach((k) => {
      items.push({ type: 'answer-key', id: k.id, label: k.tileDisplayTitle || k.examName || k.department, path: `/answer-key/${k.id}`, order: k.tickerOrder ?? 999 });
    });
    admissions.filter((ad) => ad.showInTicker && (ad.examName || ad.department)).forEach((ad) => {
      items.push({ type: 'admission', id: ad.id, label: ad.tileDisplayTitle || ad.examName || ad.department, path: `/admission/${ad.id}`, order: ad.tickerOrder ?? 999 });
    });
    if (items.length > 0) {
      return items.sort((a, b) => a.order - b.order).slice(0, 20).map(({ type, id, label, path }) => ({ type, id, label, path }));
    }
    // Fallback: last 10 jobs by date
    return jobPosts
      .filter((j) => j.title || j.department)
      .map((j) => ({ type: 'job', id: j.id, label: j.tileDisplayTitle || j.title || j.department, path: `/job/${j.id}`, ts: getDate(j.notificationDate || j.postDate) }))
      .sort((a, b) => (b.ts || 0) - (a.ts || 0))
      .slice(0, 10);
  }, [jobPosts, results, admitCards, answerKeys, admissions]);

  // Dynamic Job Posted badge: "Job posted 2 days ago" or "Posted on 15/02/2025"
  
  const isRecent = (dateString) => {
    if (!dateString) return false;
    
    // Replace space with T for Safari compatibility: "2026-05-31 16:22:27" -> "2026-05-31T16:22:27"
    let safeDateStr = typeof dateString === 'string' ? dateString.replace(' ', 'T') : dateString;
    let d = new Date(safeDateStr);
    
    if (isNaN(d.getTime()) && typeof dateString === 'string') {
      const parts = dateString.split(' ')[0].split(/[\/\-]/); // Just take the date part
      if (parts.length >= 3 && parts[0].length <= 2 && parts[2].length === 4) {
        d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}T00:00:00`); // convert DD/MM/YYYY
      }
    }
    
    if (isNaN(d.getTime())) return false;
    const now = new Date();
    const diffDays = (now - d) / (1000 * 60 * 60 * 24);
    return diffDays >= -2 && diffDays <= 7;
  };

  const RecentBadge = ({ date }) => {
    if (!isRecent(date)) return null;
    return (
      <span className="absolute top-0 right-0 bg-red-600 text-white text-[9px] px-1.5 py-0.5 rounded-bl shadow-sm font-bold uppercase tracking-wider animate-pulse z-10">
        New
      </span>
    );
  };

  const MainApp = ({ children }) => {
    const location = useLocation();
    useEffect(() => {
      const url = API_ENDPOINTS.trackVisit(location.pathname || '/');
      try { (new Image()).src = url; } catch (_) {}
    }, [location.pathname]);
    return (
      <div className={`min-h-screen flex flex-col transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
        {!children && (
          <SeoHead
            title={currentPage === 'latest-jobs' ? 'Latest Sarkari Naukri 2026 – Govt Jobs Apply Online | Sarkariresult.cloud' : currentPage === 'results' ? 'Sarkari Result 2026 – Check SSC, Railway, UPSC Result | Sarkariresult.cloud' : currentPage === 'admit-cards' ? 'Admit Card 2026 – Download Hall Ticket | SSC, Railway | Sarkariresult.cloud' : currentPage === 'answer-keys' ? 'Answer Key 2026 – Sarkari Exam Answer Key | Sarkariresult.cloud' : currentPage === 'admissions' ? 'Admissions 2026 – College Admissions | Sarkariresult.cloud' : currentPage === 'contact' ? 'Contact – Sarkariresult.cloud | Sarkari Naukri' : 'Sarkari Result 2026 – Check Latest Sarkari Result Now | Sarkari Naukri, Govt Jobs | Sarkariresult.cloud'}
            description={currentPage === 'latest-jobs' ? 'Latest Sarkari Naukri 2026 – Govt Jobs Apply Online. SSC, Railway, UPSC, Bank, State jobs. Last date, eligibility. Click to check.' : currentPage === 'results' ? 'Sarkari Result 2026 – Check SSC, Railway, UPSC, Bank Result. Sarkari Exam Result daily. Sarkariresult.cloud.' : currentPage === 'admit-cards' ? 'Admit Card 2026 – Download Hall Ticket. SSC, Railway, UPSC admit card. Sarkariresult.cloud.' : currentPage === 'answer-keys' ? 'Answer Key 2026 – Sarkari Exam Answer Key. SSC, Railway, UPSC. Sarkariresult.cloud.' : currentPage === 'admissions' ? 'Admissions 2026 – College, University admissions. Sarkari Naukri | Sarkariresult.cloud.' : currentPage === 'contact' ? 'Contact Sarkariresult.cloud – Sarkari Naukri, Govt Jobs queries.' : 'Sarkari Result 2026 – Check Latest Sarkari Result. Sarkari Naukri, Govt Jobs, SSC, Railway, UPSC, Bank, Admit Card. Sarkari Exam Result Daily. Click Now.'}
            canonical={`${SITE_URL}${location.pathname || '/'}`}
          />
        )}
        {/* Modern Sticky Header */}
        <header className="bg-[#800000] text-white shadow-md sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16 sm:h-20">
              {/* Logo & Branding */}
              <div className="flex items-center gap-3 sm:gap-4 shrink-0">
                <div className="flex-shrink-0 bg-white rounded-full p-0.5 shadow-sm">
                  <img 
                    src="/logo.png?v=2" 
                    alt="SarkariResult.cloud Logo" 
                    className="w-12 h-12 sm:w-[64px] sm:h-[64px] object-cover rounded-full block"
                  />
                </div>
                <div className="flex flex-col justify-center min-w-0">
                  <h1 className="text-lg sm:text-2xl font-bold tracking-tight text-white leading-none mb-1 uppercase">
                    SarkariResult.cloud
                  </h1>
                  <p className="text-[10px] sm:text-xs text-[#E5B8B8] font-medium tracking-wide truncate">
                    Latest Government Jobs, Results & Admit Cards
                  </p>
                </div>
              </div>

              {/* Desktop Actions & Toggle */}
              <div className="flex items-center gap-3 sm:gap-4">
                <button 
                  onClick={() => setDarkMode(!darkMode)} 
                  className="p-2 hover:bg-white/10 rounded-full transition-all duration-200 focus:outline-none" 
                  aria-label="Toggle dark mode"
                >
                  {darkMode ? <Sun className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-300" /> : <Moon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-100" />}
                </button>
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="md:hidden p-2 hover:bg-white/10 rounded-lg transition-colors focus:outline-none"
                  aria-label="Toggle mobile menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:block bg-[#660000] border-t border-white/10 shadow-inner">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex space-x-2 lg:space-x-6 h-12 items-center justify-start overflow-x-auto">
                {mainNavItems.map((item) => {
                  const isActive = currentPage === item.id || location.pathname === item.path;
                  return (
                    <RouterLink
                      key={item.id}
                      to={item.path}
                      className={`px-3 lg:px-4 py-2 rounded-md text-sm font-semibold transition-all duration-200 whitespace-nowrap ${
                        isActive 
                          ? 'bg-white text-[#800000] shadow-sm' 
                          : 'text-gray-100 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      {item.label}
                    </RouterLink>
                  );
                })}
              </div>
            </div>
          </nav>

          {/* Mobile Navigation Dropdown */}
          <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${mobileMenuOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`}>
            <nav className="bg-[#660000] border-t border-white/10 px-4 pt-2 pb-4 shadow-inner">
              <div className="space-y-1">
                {mainNavItems.map((item) => {
                  const isActive = currentPage === item.id || location.pathname === item.path;
                  return (
                    <RouterLink
                      key={item.id}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block px-3 py-3 rounded-md text-base font-medium transition-colors ${
                        isActive 
                          ? 'bg-white text-[#800000] shadow-sm' 
                          : 'text-gray-100 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      {item.label}
                    </RouterLink>
                  );
                })}
              </div>
              {/* Extra Mobile Links */}
              {hamburgerOnlyItems.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="px-3 text-xs font-semibold text-[#E5B8B8] uppercase tracking-wider mb-3">More Quick Links</p>
                  <div className="grid grid-cols-2 gap-2">
                    {hamburgerOnlyItems.map((item, idx) => (
                      <RouterLink
                        key={item.path || item.id || idx}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`block px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          location.pathname === item.path ? 'bg-white/10 text-white' : 'text-gray-200 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        {item.label}
                      </RouterLink>
                    ))}
                  </div>
                </div>
              )}
            </nav>
          </div>
        </header>

        {/* Ticker - job links only, no label */}
        <div className="bg-blue-100 border-b border-blue-200 py-2">
          <div className="max-w-7xl mx-auto px-1.5 sm:px-2 md:px-4">
            <div className="overflow-hidden min-w-0">
              {runningUpdates.length > 0 ? (
                <div className="ticker-marquee overflow-hidden">
                  <div className="ticker-sway text-xs sm:text-sm break-words [&_a]:text-blue-700 [&_a]:hover:text-blue-900 [&_a]:font-medium [&_a]:underline">
                    {runningUpdates.map((item, i) => (
                      <React.Fragment key={`${item.type}-${item.id}-${i}`}>
                        <RouterLink
                          to={item.path}
                          onMouseEnter={() => {
                            if (item.type === 'job') prefetchJob(item.id);
                            else if (item.type === 'result') prefetchResult(item.id);
                            else if (item.type === 'admit-card') prefetchAdmitCard(item.id);
                            else if (item.type === 'answer-key') prefetchAnswerKey(item.id);
                            else if (item.type === 'admission') prefetchAdmission(item.id);
                          }}
                          className="inline"
                        >
                          {item.label}
                        </RouterLink>
                        <span className="text-blue-500 select-none mx-1"> || </span>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ) : (
                <span className="text-xs sm:text-sm text-blue-800">Latest updates will appear here</span>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 w-full max-w-7xl mx-auto px-1.5 py-4 sm:px-2 sm:py-6 md:px-4 md:py-6">
          {children ? children : (
            <>
          {/* Home Page - SarkariResult style */}
          {currentPage === 'home' && (
            <div className="space-y-3 sm:space-y-5">
              {/* 8 Colored Blocks - Latest jobs (last 7 days), SarkariResult style */}
              {latest8Jobs.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                  {latest8Jobs.map((job, idx) => {
                    const colors = [
                      { bg: 'bg-green-600', sub: 'bg-red-600' },
                      { bg: 'bg-blue-700', sub: 'bg-green-500' },
                      { bg: 'bg-orange-500', sub: 'bg-pink-500' },
                      { bg: 'bg-sky-500', sub: 'bg-violet-600' },
                      { bg: 'bg-emerald-600', sub: 'bg-rose-500' },
                      { bg: 'bg-indigo-600', sub: 'bg-amber-500' },
                      { bg: 'bg-teal-600', sub: 'bg-red-500' },
                      { bg: 'bg-rose-600', sub: 'bg-blue-500' },
                    ];
                    const c = colors[idx % colors.length];
                    return (
                  <RouterLink
                      key={job.id}
                      to={`/job/${job.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onMouseEnter={() => prefetchJob(job.id)}
                      className={`${c.bg} rounded-lg overflow-hidden shadow-md hover:opacity-95 transition-opacity flex flex-col p-1 sm:p-1.5 relative items-stretch`}
                    >
                        {isRecent(job.post_date_update || job.notificationDate) && <span className={`${c.sub} badge-shine text-white text-[9px] sm:text-[10px] px-1 py-0.5 rounded font-bold uppercase absolute top-0 right-0 m-0.5 w-fit whitespace-nowrap z-10 animate-pulse shadow-sm`}>NEW</span>}
                        <span className="text-white font-semibold text-sm sm:text-base leading-none text-center pr-8 sm:pr-9 pt-0.5 block w-full">{job.tileDisplayTitle || job.title || job.department || 'Job'}</span>
                      </RouterLink>
                    );
                  })}
                </div>
              )}

              {/* 3 cols - Result, Admit Card, Latest Jobs - WIDE like SarkariResult */}
                <div className="grid grid-cols-3 gap-1.5 sm:gap-3 md:gap-4">
                    {/* Result Card */}
                    <div className="bg-white rounded-lg shadow-md overflow-hidden min-w-0">
                      <div className="bg-sarkari-red text-white px-2 py-1.5 sm:px-4 sm:py-2 font-bold text-xs sm:text-base">Result</div>
                      <ul className="p-2 sm:p-4 space-y-2 sm:space-y-3 min-h-[100px] sm:min-h-[120px]">
                        {loading ? [1,2,3].map((i) => <li key={i} className="border-b border-gray-100 pb-2 last:border-0"><div className="h-3 bg-gray-200 rounded animate-pulse w-full mb-1" /><div className="h-2 bg-gray-100 rounded animate-pulse w-2/3" /></li>) : sectionResults.slice(0, showMoreResult ? sectionResults.length : 25).map((item) => (
                          <li key={item.id} className="border-b border-gray-100 pb-2 last:border-0 last:pb-0 relative pr-6">
                            <RouterLink to={`/result/${item.id}`} className="group flex flex-col gap-0.5" onMouseEnter={() => prefetchResult(item.id)}>
                              <span className="text-blue-600 hover:underline font-medium text-xs sm:text-sm block line-clamp-2">{item.tileDisplayTitle || item.examName}</span>
                              </RouterLink>
                            <RecentBadge date={item.post_date_update || item.resultDate} />
                            <p className="text-[10px] sm:text-sm text-gray-500 mt-0.5 truncate">{item.department}</p>
                          </li>
                        ))}
                        {!loading && sectionResults.length === 0 && <li className="text-gray-500 text-xs py-1">No results</li>}
                        {!loading && sectionResults.length > 25 && (
                          <li className="pt-1">
                            <button type="button" onClick={() => setShowMoreResult(!showMoreResult)} className="text-sarkari-red hover:underline font-semibold text-xs sm:text-sm">{showMoreResult ? 'Less' : 'More'}</button>
                          </li>
                        )}
                      </ul>
                    </div>
                    {/* Admit Card */}
                    <div className="bg-white rounded-lg shadow-md overflow-hidden min-w-0">
                      <div className="bg-sarkari-red text-white px-2 py-1.5 sm:px-4 sm:py-2 font-bold text-xs sm:text-base">Admit Card</div>
                      <ul className="p-2 sm:p-4 space-y-2 sm:space-y-3 min-h-[100px] sm:min-h-[120px]">
                        {loading ? [1,2,3].map((i) => <li key={i} className="border-b border-gray-100 pb-2 last:border-0"><div className="h-3 bg-gray-200 rounded animate-pulse w-full mb-1" /><div className="h-2 bg-gray-100 rounded animate-pulse w-2/3" /></li>) : sectionAdmitCards.slice(0, showMoreAdmit ? sectionAdmitCards.length : 25).map((item) => (
                          <li key={item.id} className="border-b border-gray-100 pb-2 last:border-0 last:pb-0 relative pr-6">
                            <RouterLink to={`/admit-card/${item.id}`} className="group flex flex-col gap-0.5" onMouseEnter={() => prefetchAdmitCard(item.id)}>
                              <span className="text-blue-600 hover:underline font-medium text-xs sm:text-sm block line-clamp-2">{item.tileDisplayTitle || item.examName}</span>
                              </RouterLink>
                            <RecentBadge date={item.post_date_update || item.admitCardReleaseDate} />
                            <p className="text-[10px] sm:text-sm text-gray-500 mt-0.5 truncate">{item.department}</p>
                          </li>
                        ))}
                        {!loading && sectionAdmitCards.length === 0 && <li className="text-gray-500 text-xs py-1">No admit cards</li>}
                        {!loading && sectionAdmitCards.length > 25 && (
                          <li className="pt-1">
                            <button type="button" onClick={() => setShowMoreAdmit(!showMoreAdmit)} className="text-sarkari-red hover:underline font-semibold text-xs sm:text-sm">{showMoreAdmit ? 'Less' : 'More'}</button>
                          </li>
                        )}
                      </ul>
                    </div>
                    {/* Latest Jobs */}
                    <div className="bg-white rounded-lg shadow-md overflow-hidden min-w-0">
                      <div className="bg-sarkari-red text-white px-2 py-1.5 sm:px-4 sm:py-2 font-bold text-xs sm:text-base">Latest Jobs</div>
                      <ul className="p-2 sm:p-4 space-y-2 sm:space-y-3 min-h-[100px] sm:min-h-[120px]">
                        {loading ? [1,2,3].map((i) => <li key={i} className="border-b border-gray-100 pb-2 last:border-0"><div className="h-3 bg-gray-200 rounded animate-pulse w-full mb-1" /><div className="h-2 bg-gray-100 rounded animate-pulse w-2/3" /></li>) : latestJobsList.slice(0, showMoreJobs ? latestJobsList.length : 25).map((job) => (
                          <li key={job.id} className="border-b border-gray-100 pb-2 last:border-0 last:pb-0 relative pr-6">
                            <RouterLink
                              to={`/job/${job.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group flex flex-col gap-0.5"
                              onMouseEnter={() => prefetchJob(job.id)}
                            >
                              <span className="text-blue-600 hover:underline font-medium text-xs sm:text-sm block line-clamp-2">{job.tileDisplayTitle || job.title}</span>
                              </RouterLink>
                            <RecentBadge date={job.post_date_update || job.notificationDate} />
                            <p className="text-[10px] sm:text-sm text-gray-500 mt-0.5 truncate">{job.department}</p>
                          </li>
                        ))}
                        {!loading && latestJobsList.length === 0 && <li className="text-gray-500 text-xs py-1">No jobs</li>}
                        {!loading && latestJobsList.length > 25 && (
                          <li className="pt-1">
                            <button type="button" onClick={() => setShowMoreJobs(!showMoreJobs)} className="text-sarkari-red hover:underline font-semibold text-xs sm:text-sm">{showMoreJobs ? 'Less' : 'More'}</button>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                  {/* Answer Key (left) & Admissions (right) - parallel on all screens including mobile */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    <div className="bg-white rounded-lg shadow-md overflow-hidden min-w-0">
                      <div className="bg-sarkari-red text-white px-2 py-1.5 sm:px-4 sm:py-2 font-bold text-xs sm:text-base">Answer Key</div>
                      <ul className="p-2 sm:p-4 space-y-2">
                        {loading ? [1,2,3,4].map((i) => <li key={i} className="pb-3"><div className="h-4 bg-gray-200 rounded animate-pulse w-4/5 mb-1" /><div className="h-3 bg-gray-100 rounded animate-pulse w-1/2" /></li>) : sectionAnswerKeys.slice(0, showMoreAnswerKey ? sectionAnswerKeys.length : 4).map((item) => (
                          <li key={item.id} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0 relative pr-6">
                            <RouterLink to={`/answer-key/${item.id}`} className="group flex items-start gap-2" onMouseEnter={() => prefetchAnswerKey(item.id)}>
                              <span className="text-blue-600 hover:underline font-medium block flex-1">{item.tileDisplayTitle || item.examName}</span>
                            </RouterLink>
                            <RecentBadge date={item.post_date_update || item.answerKeyReleaseDate} />
                            <p className="text-sm text-gray-500 mt-0.5">{item.department}{item.examDate ? ` • ${new Date(item.examDate).toLocaleDateString('en-IN')}` : ''}</p>
                          </li>
                        ))}
                        {!loading && sectionAnswerKeys.length === 0 && <li className="text-gray-500 text-sm py-2">No answer keys yet</li>}
                        {!loading && sectionAnswerKeys.length > 4 && (
                          <li className="pt-2">
                            <button type="button" onClick={() => setShowMoreAnswerKey(!showMoreAnswerKey)} className="text-sarkari-red hover:underline font-semibold text-sm">{showMoreAnswerKey ? 'Less' : 'More'}</button>
                          </li>
                        )}
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg shadow-md overflow-hidden min-w-0">
                      <div className="bg-teal-600 text-white px-2 py-1.5 sm:px-4 sm:py-2 font-bold text-xs sm:text-base">Admissions</div>
                      <ul className="p-2 sm:p-4 space-y-2">
                        {loading ? [1,2,3,4].map((i) => <li key={i} className="pb-3"><div className="h-4 bg-gray-200 rounded animate-pulse w-4/5 mb-1" /><div className="h-3 bg-gray-100 rounded animate-pulse w-1/2" /></li>) : sectionAdmissions.slice(0, showMoreAdmission ? sectionAdmissions.length : 4).map((item) => (
                          <li key={item.id} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                            <RouterLink to={`/admission/${item.id}`} className="group flex items-start gap-2" onMouseEnter={() => prefetchAdmission(item.id)}>
                              <span className="text-blue-600 hover:underline font-medium block flex-1">{item.tileDisplayTitle || item.examName}</span>
                              <span className="bg-teal-600 text-white text-xs px-2 py-0.5 rounded font-semibold shrink-0 whitespace-nowrap">{item.examDate ? new Date(item.examDate).toLocaleDateString('en-IN') : 'Admission'}</span>
                            </RouterLink>
                            <p className="text-sm text-gray-500 mt-0.5">{item.department}{item.lastDate ? ` • Last: ${new Date(item.lastDate).toLocaleDateString('en-IN')}` : ''}</p>
                          </li>
                        ))}
                        {!loading && sectionAdmissions.length === 0 && <li className="text-gray-500 text-sm py-2">No admissions yet</li>}
                        {!loading && sectionAdmissions.length > 4 && (
                          <li className="pt-2">
                            <button type="button" onClick={() => setShowMoreAdmission(!showMoreAdmission)} className="text-sarkari-red hover:underline font-semibold text-sm">{showMoreAdmission ? 'Less' : 'More'}</button>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>

              {/* SEO paragraph - niche (moved from top), Sarkari Result style */}
              <section className={`rounded-lg p-4 sm:p-5 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`} aria-label="About Sarkari Result">
                <h2 className={`text-base sm:text-lg font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Sarkari Result – Sarkariresult | Latest Sarkari Naukri, Govt Jobs, Admit Card & Answer Key 2026
                </h2>
                <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <RouterLink to="/sarkari-result" className="text-sarkari-red font-bold hover:underline">Sarkari Result</RouterLink>
                  {' '}ya <strong>Sarkariresult</strong> check karo <strong>Sarkariresult.cloud</strong> par. Sarkari Result 2026, Latest{' '}
                  <RouterLink to="/sarkari-naukri" className="text-sarkari-red font-bold hover:underline">Sarkari Naukri</RouterLink>
                  , <RouterLink to="/ssc-result" className="text-sarkari-red font-semibold hover:underline">SSC Result</RouterLink>
                  , <RouterLink to="/railway-result" className="text-sarkari-red font-semibold hover:underline">Railway Result</RouterLink>
                  , <RouterLink to="/upsc-result" className="text-sarkari-red font-semibold hover:underline">UPSC Result</RouterLink>
                  , <RouterLink to="/bank-jobs" className="text-sarkari-red font-semibold hover:underline">Bank Jobs</RouterLink>
                  , <RouterLink to="/up-police-result" className="text-sarkari-red font-semibold hover:underline">UP Police Result</RouterLink>
                  – sab Sarkari Result updates ek jagah.
                </p>
              </section>
            </div>
          )}

          {/* Results Page - same layout as Latest Jobs */}
          {currentPage === 'results' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <h1 className={`text-xl sm:text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Sarkari Result – Latest Results 2026</h1>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                  <Clock className="w-4 h-4" />
                  Updated {new Date().toLocaleDateString()}
                </div>
              </div>
              <section className={`rounded-lg p-4 sm:p-5 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                <h2 className={`text-base sm:text-lg font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Check Sarkari Result, Sarkari Exam Result Daily</h2>
                <p className={`text-sm leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <strong>Sarkariresult.cloud</strong> provides the latest <strong>Sarkari Result</strong> and <strong>Sarkari Exam Result</strong> updates for SSC, Railway, UPSC, State PSC, and other government exams. <RouterLink to="/sarkari-result-check" className="text-sarkari-red font-semibold hover:underline">Sarkari Result Check</RouterLink> karein yahan – we list results as soon as they are declared by the respective boards.
                </p>
                <p className={`text-sm leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <RouterLink to="/ssc-result" className="text-sarkari-red font-semibold hover:underline">SSC Result</RouterLink>, <RouterLink to="/railway-result" className="text-sarkari-red font-semibold hover:underline">Railway Result</RouterLink>, <RouterLink to="/upsc-result" className="text-sarkari-red font-semibold hover:underline">UPSC Result</RouterLink>, and <RouterLink to="/up-police-result" className="text-sarkari-red font-semibold hover:underline">UP Police Result</RouterLink> – all Sarkari Result links in one place. Sarkariresult | Sarkari Naukri | Govt Jobs.
                </p>
                <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  For accurate marks and merit list, always verify on the official website. Sarkariresult.cloud aggregates Sarkari Result notifications for your convenience.
                </p>
              </section>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <input
                  type="text"
                  value={resultSearch}
                  onChange={(e) => setResultSearch(e.target.value)}
                  placeholder="Search by exam name or department..."
                  className={`flex-1 min-w-[200px] px-4 py-2.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                />
                <select
                  value={resultSortBy}
                  onChange={(e) => setResultSortBy(e.target.value)}
                  className={`px-4 py-2.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="recent">Newest First</option>
                  <option value="dept">Department A-Z</option>
                  <option value="date">Result Date</option>
                </select>
              </div>
              {loading ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sarkari-red"></div></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {getFilteredAndSortedResults().map((item) => (
                    <ResultSectionCard key={item.id} item={item} />
                  ))}
                </div>
              )}
              {!loading && getFilteredAndSortedResults().length === 0 && (
                <p className="text-center text-gray-500 py-8">No results found</p>
              )}
            </div>
          )}

          {/* Admit Cards Page - same layout as Latest Jobs */}
          {currentPage === 'admit-cards' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <h1 className={`text-xl sm:text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Sarkari Result – Admit Card 2026</h1>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                  <Clock className="w-4 h-4" />
                  Updated {new Date().toLocaleDateString()}
                </div>
              </div>
              <section className={`rounded-lg p-4 sm:p-5 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                <h2 className={`text-base sm:text-lg font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Download Admit Card – Hall Ticket for Sarkari Exams</h2>
                <p className={`text-sm leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <strong>Sarkariresult.cloud</strong> lists the latest <strong>Admit Card</strong> and hall ticket release updates for government exams. Admit Card is mandatory for appearing in any Sarkari exam – download it from the official website before the exam date.
                </p>
                <p className={`text-sm leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  SSC Admit Card, Railway Admit Card, UPSC Admit Card, State PSC Admit Card – we update as soon as admit cards are released. Check <RouterLink to="/latest-jobs" className="text-sarkari-red font-semibold hover:underline">Latest Jobs</RouterLink> for new recruitment and <RouterLink to="/results" className="text-sarkari-red font-semibold hover:underline">Sarkari Result</RouterLink> for exam results. Sarkariresult | Sarkari Naukri.
                </p>
                <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Always verify your details on the admit card. For corrections, contact the exam conducting body before the last date.
                </p>
              </section>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <input
                  type="text"
                  value={admitSearch}
                  onChange={(e) => setAdmitSearch(e.target.value)}
                  placeholder="Search by exam name or department..."
                  className={`flex-1 min-w-[200px] px-4 py-2.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                />
                <select
                  value={admitSortBy}
                  onChange={(e) => setAdmitSortBy(e.target.value)}
                  className={`px-4 py-2.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="recent">Newest First</option>
                  <option value="dept">Department A-Z</option>
                  <option value="date">Exam Date</option>
                </select>
              </div>
              {loading ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sarkari-red"></div></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {getFilteredAndSortedAdmitCards().map((item) => (
                    <AdmitCardSectionCard key={item.id} item={item} />
                  ))}
                </div>
              )}
              {!loading && getFilteredAndSortedAdmitCards().length === 0 && (
                <p className="text-center text-gray-500 py-8">No admit cards found</p>
              )}
            </div>
          )}

          {/* Answer Keys Page - same layout as Latest Jobs */}
          {currentPage === 'answer-keys' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <h1 className={`text-xl sm:text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Sarkari Result – Answer Key 2026</h1>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                  <Clock className="w-4 h-4" />
                  Updated {new Date().toLocaleDateString()}
                </div>
              </div>
              <section className={`rounded-lg p-4 sm:p-5 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                <h2 className={`text-base sm:text-lg font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Answer Key – Objection & Final Answer Key</h2>
                <p className={`text-sm leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <strong>Sarkariresult.cloud</strong> provides <strong>Answer Key</strong> updates for SSC, Railway, UPSC, and other Sarkari exams. Provisional Answer Key is released first – candidates can raise objections. Final Answer Key is published after the objection window.
                </p>
                <p className={`text-sm leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Check <RouterLink to="/results" className="text-sarkari-red font-semibold hover:underline">Sarkari Result</RouterLink> for final results and <RouterLink to="/admit-card" className="text-sarkari-red font-semibold hover:underline">Admit Card</RouterLink> for hall tickets. Answer Key helps you estimate your score before the Sarkari Result is declared. Sarkariresult | Sarkari Naukri | Govt Jobs.
                </p>
                <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Submit objections before the last date if you find errors in the provisional Answer Key. Official websites have the final say.
                </p>
              </section>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <input
                  type="text"
                  value={answerSearch}
                  onChange={(e) => setAnswerSearch(e.target.value)}
                  placeholder="Search by exam name or department..."
                  className={`flex-1 min-w-[200px] px-4 py-2.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                />
                <select
                  value={answerSortBy}
                  onChange={(e) => setAnswerSortBy(e.target.value)}
                  className={`px-4 py-2.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="recent">Newest First</option>
                  <option value="dept">Department A-Z</option>
                  <option value="date">Exam Date</option>
                </select>
              </div>
              {loading ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sarkari-red"></div></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {getFilteredAndSortedAnswerKeys().map((item) => (
                    <AnswerKeySectionCard key={item.id} item={item} />
                  ))}
                </div>
              )}
              {!loading && getFilteredAndSortedAnswerKeys().length === 0 && (
                <p className="text-center text-gray-500 py-8">No answer keys found</p>
              )}
            </div>
          )}

          {/* Admissions Page - same layout as Latest Jobs */}
          {currentPage === 'admissions' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                <h1 className={`text-xl sm:text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Admissions 2026</h1>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                  <Clock className="w-4 h-4" />
                  Updated {new Date().toLocaleDateString()}
                </div>
              </div>
              <section className={`rounded-lg p-4 sm:p-5 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                <h2 className={`text-base sm:text-lg font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Admissions – College, University & Govt Admissions</h2>
                <p className={`text-sm leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <strong>Sarkariresult.cloud</strong> lists the latest <strong>Admissions</strong> for colleges, universities, and government institutes. Apply before the last date. Check <RouterLink to="/latest-jobs" className="text-sarkari-red font-semibold hover:underline">Latest Jobs</RouterLink> for recruitment and <RouterLink to="/results" className="text-sarkari-red font-semibold hover:underline">Sarkari Result</RouterLink> for exam results.
                </p>
                <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Sarkari Naukri | Sarkari Result | Govt Jobs | Admissions 2026.
                </p>
              </section>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <input
                  type="text"
                  value={admissionSearch}
                  onChange={(e) => setAdmissionSearch(e.target.value)}
                  placeholder="Search by exam name or department..."
                  className={`flex-1 min-w-[200px] px-4 py-2.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                />
                <select
                  value={admissionSortBy}
                  onChange={(e) => setAdmissionSortBy(e.target.value)}
                  className={`px-4 py-2.5 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="recent">Newest First</option>
                  <option value="dept">Department A-Z</option>
                  <option value="date">Exam Date</option>
                </select>
              </div>
              {loading ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sarkari-red"></div></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {getFilteredAndSortedAdmissions().map((item) => (
                    <AdmissionsSectionCard key={item.id} item={item} />
                  ))}
                </div>
              )}
              {!loading && getFilteredAndSortedAdmissions().length === 0 && (
                <p className="text-center text-gray-500 py-8">No admissions found</p>
              )}
            </div>
          )}

          {/* Contact Page */}
          {currentPage === 'contact' && (
            <div className={`p-8 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
              <h2 className={`text-2xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Contact – Sarkariresult.cloud | Sarkari Result</h2>
              <p className={`leading-relaxed mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <strong>Sarkariresult.cloud</strong> – Sarkari Result, Sarkariresult | Sarkari Naukri. For any queries about Sarkari Result, Govt Jobs, please reach out to us.
              </p>
              <p className={darkMode ? 'text-gray-400 text-sm' : 'text-gray-500 text-sm'}>support@sarkarijob.com</p>
            </div>
          )}

          {/* Latest Jobs Page - Category authority with 300+ words intro */}
          {currentPage === 'latest-jobs' && (
              <div className="space-y-4 sm:space-y-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                  <h1 className={`text-xl sm:text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Sarkari Naukri – Latest Jobs 2026
                  </h1>
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    Updated {new Date().toLocaleDateString()}
                  </div>
                </div>
                <section className={`rounded-lg p-4 sm:p-5 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                  <h2 className={`text-base sm:text-lg font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Latest Sarkari Naukri & Government Jobs 2026</h2>
                  <p className={`text-sm leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    <strong>Sarkariresult.cloud</strong> brings you the latest <strong>Sarkari Naukri</strong> and government job notifications from across India. Whether you are looking for <RouterLink to="/ssc-result" className="text-sarkari-red font-semibold hover:underline">SSC jobs</RouterLink>, <RouterLink to="/railway-result" className="text-sarkari-red font-semibold hover:underline">Railway recruitment</RouterLink>, <RouterLink to="/upsc-result" className="text-sarkari-red font-semibold hover:underline">UPSC exams</RouterLink>, <RouterLink to="/bank-jobs" className="text-sarkari-red font-semibold hover:underline">Bank jobs</RouterLink>, or <RouterLink to="/up-police-result" className="text-sarkari-red font-semibold hover:underline">UP Police</RouterLink> vacancies, we update our list daily with new Sarkari Naukri posts.
                  </p>
                  <p className={`text-sm leading-relaxed mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Apply online before the last date. Each job listing includes important dates, application fee, age limit, eligibility, and direct Apply Online links. Check <RouterLink to="/sarkari-result" className="text-sarkari-red font-semibold hover:underline">Sarkari Result</RouterLink> for exam results and <RouterLink to="/admit-card" className="text-sarkari-red font-semibold hover:underline">Admit Card</RouterLink> for hall tickets. <strong>Sarkariresult</strong> | <strong>Sarkari Naukri</strong> | Govt Jobs 2026.
                  </p>
                  <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    State-wise jobs, central government jobs, PSU recruitment, and defence sector vacancies are all covered. Bookmark Sarkariresult.cloud for daily Sarkari Naukri updates.
                  </p>
                </section>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {getFilteredAndSortedJobs().map((job) => (
                    <LatestJobCard key={job.id} job={job} />
                  ))}
                </div>
              </div>
            )}

            {/* Other Pages */}
            {currentPage === 'profile' && (
              <div className="space-y-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="text-6xl">{user.avatar}</div>
                  <div>
                    <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {user.name} ⚡
                    </h2>
                    <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                      {user.email}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {(currentPage === 'photo-resizer' || currentPage === 'jpg-to-pdf') && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">
                  {currentPage === 'photo-resizer' ? '📷' : '📄'}
                </div>
                <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {currentPage === 'photo-resizer' ? 'Photo Resizer ⚡' : 'JPG to PDF Converter ⚡'}
                </h2>
                <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Student tool functionality working perfectly!
                </p>
              </div>
            )}
            </>
            )}
          </main>

          <SeoFooter darkMode={darkMode} />
      </div>
    );
  };

  // Minimal route fallback (keeps LCP fast)
  const RouteFallback = () => (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sarkari-red" />
    </div>
  );
  // 404 - unknown routes (e.g. wrong link)
  const NotFoundRoute = ({ darkMode }) => (
      <div className={`min-h-screen flex flex-col items-center justify-center px-4 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <h1 className="text-2xl font-bold mb-2">Page Not Found</h1>
        <p className="text-sm mb-4 opacity-80">Ye page maujood nahi hai. Home par wapas jao.</p>
        <RouterLink to="/" className="bg-sarkari-red text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700">Home</RouterLink>
      </div>
    );
  // ✅ Admin via ?admin=1 (works without server routing - no 404)
  const AdminOrHome = () => {
    const [sp] = useSearchParams();
    if (sp.get('admin') === '1') return <AdminTableManager />;
    return <MainApp />;
  };
  // ✅ ROUTER SETUP - lazy chunks for landing pages (Router in App.js)
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
          <Route path="/" element={<AdminOrHome />} />
          <Route path="/latest-jobs" element={<AdminOrHome />} />
          <Route path="/results" element={<AdminOrHome />} />
          <Route path="/admit-card" element={<AdminOrHome />} />
          <Route path="/answer-key" element={<AdminOrHome />} />
          <Route path="/admissions" element={<AdminOrHome />} />
          <Route path="/contact" element={<AdminOrHome />} />
          <Route path="/sarkari-result" element={<SarkariResultLanding darkMode={darkMode} />} />
          <Route path="/sarkari-result-check" element={<SarkariResultCheckLanding darkMode={darkMode} />} />
          <Route path="/latest-sarkari-result" element={<LatestSarkariResultLanding darkMode={darkMode} />} />
          <Route path="/sarkari-naukri" element={<SarkariNaukriLanding darkMode={darkMode} />} />
          <Route path="/ssc-result" element={<CategoryLanding darkMode={darkMode} slug="ssc-result" />} />
          <Route path="/railway-result" element={<CategoryLanding darkMode={darkMode} slug="railway-result" />} />
          <Route path="/railway-jobs" element={<Navigate to="/railway-result" replace />} />
          <Route path="/upsc-result" element={<CategoryLanding darkMode={darkMode} slug="upsc-result" />} />
          <Route path="/bank-jobs" element={<CategoryLanding darkMode={darkMode} slug="bank-jobs" />} />
          <Route path="/up-police-result" element={<CategoryLanding darkMode={darkMode} slug="up-police-result" />} />
          <Route path="/teaching-jobs" element={<CategoryLanding darkMode={darkMode} slug="teaching-jobs" />} />
          <Route path="/state/:stateSlug" element={<StateLanding darkMode={darkMode} />} />
          <Route path="/job/:jobId" element={<MainApp><JobDetailPage /></MainApp>} />
          <Route path="/result/:resultId" element={<MainApp><ResultDetailPage /></MainApp>} />
          <Route path="/admit-card/:admitCardId" element={<MainApp><AdmitCardDetailPage /></MainApp>} />
          <Route path="/answer-key/:answerKeyId" element={<MainApp><AnswerKeyDetailPage /></MainApp>} />
          <Route path="/admission/:admissionId" element={<MainApp><AdmissionDetailPage /></MainApp>} />
          <Route path="/admin" element={<AdminTableManager />} />
          <Route path="/admin-table-manager" element={<AdminTableManager />} />
          <Route path="/wp-admin" element={<WordPressStyleAdmin />} />
          <Route path="*" element={<NotFoundRoute darkMode={darkMode} />} />
      </Routes>
    </Suspense>
  );
};

export default GovJobsPortal;
